import "./planning-groups.css";
import { Show, EditButton, DeleteButton, useTable } from "@refinedev/antd";
import { useShow, useDelete, useInvalidate, useGetIdentity } from "@refinedev/core";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router";
import { App, Row, Col, Card, Collapse, Statistic, Progress, Space, Tabs, Segmented, Tag, Table, Button, Popconfirm, message, Modal, DatePicker, Form, Select, Input, Tooltip, Drawer, Typography } from "antd";

const { Text } = Typography;
import type { Dayjs } from "dayjs";
import {
  AuditOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  EnvironmentOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  StopOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  SearchOutlined,
  UserOutlined,
  CloudDownloadOutlined,
  ReloadOutlined,
  CalendarOutlined,
  ApartmentOutlined,
  ClearOutlined,
  CommentOutlined,
  InfoCircleOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import type { PlanningGroupDetail } from "../../types";
import type { Audit } from "../../types/audit";
import type { UserInfo } from "../../types/auth";
import { ADMIN_USER_TYPES, isEurosemillasScoped } from "../../types/auth";
import type { Route } from "../../types/route";
import { usePageTitle } from "../../hooks";
import { AuditCreateModal } from "./components/AuditCreateModal";
import { AuditsByAuditorChart } from "./components/AuditsByAuditorChart";
import { PlanningCommentsPanel } from "./components/PlanningCommentsPanel";
import { AuditEditModal } from "./components/AuditEditModal";
import { CancelAuditModal } from "./components/CancelAuditModal";
import { RouteUploadModal } from "./components/RouteUploadModal";
import { RouteEditModal } from "./components/RouteEditModal";
import { WaypointEditModal } from "./components/WaypointEditModal";
import { UploadWaypointsModal } from "./components/UploadWaypointsModal";
import { WaypointDetailModal } from "./components/WaypointDetailModal";
import { AssignAuditorModal } from "./components/AssignAuditorModal";
import { BulkAssignAuditorModal } from "./components/BulkAssignAuditorModal";
import {
  downloadAuditBundle,
  isBundleSupported,
} from "../../services/api/auditBundleService";
import dayjs from "dayjs";

type AssignedFilter =
  | "all"
  | "unassigned"
  | "assigned"
  | "planificada"
  | "performed"          // auditor la ha trabajado pero no la ha cerrado
  | "submitted"          // auditor cerró; admin pendiente de revisar
  | "finalizada"
  | "cancelada";

/** Orden de los tabs en el filtro de estado. Debe coincidir con el
 *  flujo lógico de la auditoría: crear → asignar → planificar → auditor
 *  trabaja → auditor cierra → admin finaliza → (o cancela). */
const ASSIGNED_FILTER_ORDER: AssignedFilter[] = [
  "unassigned",
  "assigned",
  "planificada",
  "performed",
  "submitted",
  "finalizada",
  "cancelada",
  "all",
];

/**
 * True when `audit` should be included in the Segmented bucket `f`.
 *
 * Every bucket maps to the *derived* display state (`getDisplayState`)
 * so the counts are mutually exclusive — each audit falls into exactly
 * one of {Sin asignar, Asignada, Planificada, Finalizada, Cancelada}.
 * Otherwise the catalogue value (always "Planificada" for non-closed
 * audits) would double-count the unassigned/assigned rows under the
 * Planificada tab.
 */
const matchesAssignedFilter = (audit: any, f: AssignedFilter): boolean => {
  if (f === "all") return true;
  const display = getDisplayState(audit);
  const lowered = display.toLowerCase();
  if (f === "unassigned") return display === "Sin asignar";
  if (f === "assigned") return display === "Asignada";
  if (f === "planificada") return display === "Planificada";
  if (f === "performed") return display === "Realizada";
  if (f === "submitted") return display === "Pendiente revisión";
  if (f === "finalizada") return lowered.includes("finaliz");
  if (f === "cancelada") return lowered.includes("cancel");
  return false;
};

/**
 * Resolve the *displayed* state of an audit.
 *
 * Doble fuente de verdad:
 *   1. `status_of_audit` (backend) — status técnico del ciclo de vida:
 *      not_planned · planned · performed · submitted · finalized · canceled.
 *      Es el campo que hay que consultar primero — cambia con acciones
 *      concretas (auditor firma acta, admin finaliza, coord cancela).
 *   2. `audit_state_name` (catálogo) — etiqueta legible ("Planificada",
 *      "Finalizada", "Cancelada"). Siempre está en "Planificada" hasta
 *      que status_of_audit transiciona a finalized/canceled, así que
 *      NO discrimina entre "sin asignar", "asignada" y realmente
 *      planificada — para eso usamos los campos derivados.
 *
 * Reglas (en orden de precedencia):
 *   - status = canceled                       → «Cancelada»
 *   - status = finalized                      → «Finalizada»
 *   - status = submitted                      → «Pendiente revisión»
 *                                              (auditor cerró; admin
 *                                               pendiente de finalizar
 *                                               o devolver)
 *   - status = performed                      → «Realizada» (auditor
 *                                              en curso, todavía no
 *                                              cerrada)
 *   - status = planned + no auditor           → «Sin asignar»
 *   - status = planned + auditor + no date    → «Asignada»
 *   - status = planned + auditor + date       → «Planificada»
 *   - fallback (raro)                         → audit_state_name tal cual
 */
const getDisplayState = (audit: any): string => {
  const status = (audit?.status_of_audit || "").toLowerCase();
  if (status === "canceled") return "Cancelada";
  if (status === "finalized") return "Finalizada";
  if (status === "submitted") return "Pendiente revisión";
  if (status === "performed") return "Realizada";
  // Fallback al audit_state_name para not_planned/planned + antiguos.
  const raw = (audit?.audit_state_name || "").trim();
  if (!raw) return "";
  const lowered = raw.toLowerCase();
  if (!lowered.includes("planific")) return raw;
  if (audit.auditor_user == null) return "Sin asignar";
  if (!audit.planning_date) return "Asignada";
  return raw;
};

/** Antd `Tag` colour for a given derived state string. */
const colorForDisplayState = (display: string): string => {
  const lowered = display.toLowerCase();
  if (display === "Sin asignar") return "gold";
  if (display === "Asignada") return "cyan";
  if (display === "Realizada") return "processing";
  if (display === "Pendiente revisión") return "orange";
  if (lowered.includes("cancel")) return "red";
  if (lowered.includes("finaliz")) return "green";
  if (lowered.includes("planific")) return "blue";
  return "default";
};

/**
 * PlanningGroupShow Component
 * Displays detailed information about a planning group including audit statistics and audit table
 */
export const PlanningGroupShow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Context-aware versions of message + modal — the static ones
  // (`Modal.confirm`, `message.success`) work but skip the
  // ConfigProvider theme/context, which can fail silently inside
  // some wrappers. Use these for any new dialog we add.
  const { modal: appModal, message: appMessageCtx } = App.useApp();
  const location = useLocation();
  const invalidate = useInvalidate();
  // Refine's automatic id-from-URL only works for routes that match the
  // resource's registered `show` path (/planning-groups/show/:id). The
  // auditor section uses /auditor/planning-groups/:id, so we must read
  // the id from useParams and pass it explicitly.
  const { id: idParam } = useParams<{ id?: string }>();
  const { query } = useShow<PlanningGroupDetail>({
    resource: "planning-groups",
    id: idParam,
  });

  // Identity + role gates. user_type=5 (regular auditor) only ever sees
  // their own data, so the auditor filter is forced to their app_user.id
  // and the dropdown is hidden. Coordinators/admins get a free dropdown.
  const { data: identity } = useGetIdentity<UserInfo>();
  const myAppUserId = identity?.app_user?.id ?? null;
  const myAppUserName = identity?.app_user?.name ?? "";
  // Shared payload passed down to the route/waypoint modals so their
  // auditor picker can lock to the current user when isAuditorOnly.
  const currentAppUser =
    myAppUserId != null ? { id: myAppUserId, name: myAppUserName } : null;
  const userType = identity?.app_user?.user_type ?? -1;
  const isAuditorOnly = userType === 5;
  /**
   * `isAdmin` en esta pantalla se usa como puerta para acciones de
   * escritura (crear/editar/cancelar/asignar auditor…). Los ESA Admin
   * scoped (Eurosemillas) son solo-lectura por política, así que los
   * excluimos aquí y toda la cadena de gates ya existente los oculta
   * sin tener que tocar cada botón.
   */
  const isAdmin =
    (ADMIN_USER_TYPES as readonly number[]).includes(userType)
    && !isEurosemillasScoped(identity);
  const isCoordinator = userType === 4;
  const canPickAuditor = !isAuditorOnly;
  // Auditor filter: numeric id for a concrete auditor, "all" for no
  // filter, or "unassigned" to surface audits without auditor_user
  // (a coordinator-requested option so they can spot work that's
  // still pending assignment).
  const [auditorFilter, setAuditorFilter] = useState<number | "all" | "unassigned">("all");

  const { data, isLoading } = query;
  const record = data?.data;
  
  // Set page title
  usePageTitle(record?.name || "Planificación");

  // Modal states
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const [routeUploadModalVisible, setRouteUploadModalVisible] = useState(false);
  const [routeEditModalVisible, setRouteEditModalVisible] = useState(false);
  const [routeToEdit, setRouteToEdit] = useState<any>(null);
  const [waypointEditModalVisible, setWaypointEditModalVisible] = useState(false);
  const [waypointToEdit, setWaypointToEdit] = useState<any>(null);
  const [waypointUploadModalVisible, setWaypointUploadModalVisible] = useState(false);
  const [waypointDetailModalVisible, setWaypointDetailModalVisible] = useState(false);
  const [selectedWaypointId, setSelectedWaypointId] = useState<number | null>(null);
  const [assignAuditorModalVisible, setAssignAuditorModalVisible] = useState(false);
  const [auditToAssign, setAuditToAssign] = useState<Audit | null>(null);
  const [cancelAuditModalVisible, setCancelAuditModalVisible] = useState(false);
  const [auditToCancel, setAuditToCancel] = useState<Audit | null>(null);

  // Selección múltiple para asignación masiva de auditor (admin +
  // auditor jefe). Se rellena con los ids de las filas marcadas en
  // el checkbox de la columna izquierda del Table. Cuando hay al menos
  // una fila seleccionada aparece la toolbar de bulk.
  const [selectedAuditIds, setSelectedAuditIds] = useState<number[]>([]);
  const [bulkAssignModalVisible, setBulkAssignModalVisible] = useState(false);

  // Sincronización con la URL — declaramos `searchParams` aquí porque
  // varios filtros/paginación de más abajo se apoyan en él. Al volver
  // desde el detalle de una auditoría con el back del navegador la
  // query se conserva y todos los controles se restauran solos.
  const [searchParams, setSearchParams] = useSearchParams();

  // Search filters (client-side)
  const auditSearchText = searchParams.get("q") ?? "";
  const setAuditSearchText = (value: string) => {
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        prev.delete("auditPage");
        prev.delete("page");
        return prev;
      },
      { replace: true },
    );
  };
  const [debouncedAuditSearchText, setDebouncedAuditSearchText] = useState("");
  // Assignment-state quick toggle (Sin asignar / Asignadas / Todas).
  // Sits in the table toolbar so the user flips between pending and
  // done work without opening the column-header filter on Auditor.
  // Filtro de estado sincronizado con la URL — al volver del detalle
  // de una auditoría con el back del navegador, el estado seleccionado
  // se restaura solo. Valor por defecto "all" (mostrar todas).
  const assignedFilterUrl = searchParams.get("state") as AssignedFilter | null;
  const assignedFilter: AssignedFilter =
    assignedFilterUrl && ASSIGNED_FILTER_ORDER.includes(assignedFilterUrl)
      ? assignedFilterUrl
      : "all";
  const setAssignedFilter = (value: AssignedFilter) => {
    setSearchParams(
      (prev) => {
        if (value === "all") prev.delete("state");
        else prev.set("state", value);
        // Al cambiar de filtro, resetear a página 1 (la página
        // anterior puede quedar fuera de rango con menos filas).
        prev.delete("auditPage");
        prev.delete("page");
        return prev;
      },
      { replace: true },
    );
  };
  // Filtro por estado de envío del aforo a la web de certificación —
  // las filas con `aforo_synced_at != null` se pintan de verde en la
  // tabla. Este Segmented permite acotar la vista rápida sin abrir el
  // dropdown de una columna. Persistido en la URL como `?aforo=sent|pending`.
  type AforoFilter = "all" | "sent" | "pending";
  const AFORO_FILTER_VALUES: AforoFilter[] = ["all", "sent", "pending"];
  const aforoFilterUrl = searchParams.get("aforo") as AforoFilter | null;
  const aforoFilter: AforoFilter =
    aforoFilterUrl && AFORO_FILTER_VALUES.includes(aforoFilterUrl)
      ? aforoFilterUrl
      : "all";
  const setAforoFilter = (value: AforoFilter) => {
    setSearchParams(
      (prev) => {
        if (value === "all") prev.delete("aforo");
        else prev.set("aforo", value);
        prev.delete("auditPage");
        prev.delete("page");
        return prev;
      },
      { replace: true },
    );
  };
  // Bumped by "Limpiar filtros" — used in the Table's `key` so antd's
  // internally-owned column-filter state resets cleanly on click.
  const [tableResetCounter, setTableResetCounter] = useState(0);
  // True while the xlsx download is in flight; powers the loading
  // state on the Exportar Excel button.
  const [exportingAudits, setExportingAudits] = useState(false);
  const [routeSearchText, setRouteSearchText] = useState("");
  const [waypointSearchText, setWaypointSearchText] = useState("");
  // Loading state por fila para el botón "Reprocesar waypoints" —
  // bloquea el botón individual mientras el endpoint bulk se ejecuta,
  // sin bloquear el resto de la tabla.
  const [relookupingWpId, setRelookupingWpId] = useState<number | null>(null);

  // Debounce audit search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAuditSearchText(auditSearchText);
      setAuditCurrentPage(1); // Reset to first page when search changes
    }, 500); // Wait 500ms after user stops typing

    return () => {
      clearTimeout(timer);
    };
  }, [auditSearchText]);

  // Audit table - load all audits at once for client-side filtering/sorting/pagination
  const [allAudits, setAllAudits] = useState<Audit[]>([]);
  const [loadingAudits, setLoadingAudits] = useState(false);
  // Persistimos page + pageSize en la query string para que al volver
  // desde el detalle de una auditoría (Atrás del navegador) la tabla
  // restaure la posición exacta en la que estaba el usuario. Sin esto
  // la tabla vuelve siempre a la página 1. `searchParams` ya está
  // declarado arriba (junto al resto de filtros URL-sincronizados).
  // URL params dedicados por tabla para que las paginaciones sean
  // 100% independientes. Antes usábamos `page`/`pageSize` genéricos,
  // que colisionaban con el useTable de Refine de routes/waypoints
  // (que en ciertos casos leen esos mismos keys aun con
  // `syncWithLocation: false`). Con nombres únicos cada tabla
  // conserva su propia página y tamaño sin arrastrarse entre sí.
  const [auditCurrentPage, setAuditCurrentPage] = useState(() => {
    // Compat: fallback al legacy `page` si viene de un enlace externo
    // guardado antes del rename.
    const n = Number(
      searchParams.get("auditPage") ?? searchParams.get("page"),
    );
    return Number.isFinite(n) && n > 0 ? n : 1;
  });
  const [auditPageSize, setAuditPageSize] = useState(() => {
    const n = Number(
      searchParams.get("auditPageSize") ?? searchParams.get("pageSize"),
    );
    return Number.isFinite(n) && n > 0 ? n : 15;
  });

  // Estado LOCAL propio para las paginaciones de rutas y waypoints.
  // No van a URL — mantener el estado en memoria evita cualquier
  // colisión con la URL de audits y con Refine. Al cambiar de
  // planning-group la página del componente cambia y estos estados
  // se reinician con el remount.
  const [routeCurrentPage, setRouteCurrentPage] = useState(1);
  const [routePageSize, setRoutePageSize] = useState(10);
  const [waypointCurrentPage, setWaypointCurrentPage] = useState(1);
  const [waypointPageSize, setWaypointPageSize] = useState(10);

  // --- Persistencia de filtros de columna + orden en la URL --------
  // Los filtros de columna (icono de embudo) se serializan como
  // `f_<col>=v1,v2` y el orden como `sort=<col>:asc|desc`. Se leen al
  // montar para restaurar el estado tras volver desde el detalle de
  // una auditoría (mismo mecanismo que page/pageSize/state/q). Se
  // sincronizan en el `onChange` del Table.
  const columnFiltersFromUrl: Record<string, string[]> = (() => {
    const acc: Record<string, string[]> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith("f_") && value) {
        acc[key.slice(2)] = value.split(",");
      }
    });
    return acc;
  })();
  const sorterFromUrl: { field: string; order: "ascend" | "descend" } | null = (() => {
    const raw = searchParams.get("sort");
    if (!raw) return null;
    const [field, orderStr] = raw.split(":");
    if (!field) return null;
    if (orderStr === "asc") return { field, order: "ascend" };
    if (orderStr === "desc") return { field, order: "descend" };
    return null;
  })();
  // Helper que se spread-ea en cada `<Table.Column ...>` con filter
  // o sort para hacer que el column state sea "controlled" contra la
  // URL (y por tanto se restaure al volver atrás).
  const columnStateProps = (dataIndex: string) => ({
    filteredValue: columnFiltersFromUrl[dataIndex] ?? null,
    sortOrder:
      sorterFromUrl?.field === dataIndex ? sorterFromUrl.order : (null as any),
  });
  // Handler ÚNICO del Table de auditorías — Ant Design dispara este
  // `onChange` con la pagination + filters + sorter actualizados en
  // el MISMO tick. Antes teníamos también un `pagination.onChange`
  // separado; los dos handlers escribían `setSearchParams` con un
  // snapshot stale de la URL, así que se pisaban mutuamente y las
  // páginas nunca cambiaban de verdad. Con un solo punto de escritura
  // y `setSearchParams(prev => ...)` (función, no snapshot) evitamos
  // el race y la paginación funciona con o sin filtros.
  const handleAuditsTableChange = (
    pagination: any,
    filters: Record<string, unknown[] | null>,
    sorter: any,
  ) => {
    // Compara filtros nuevos vs los que hay en la URL para saber si
    // el user tocó algún filtro (implica reset a página 1).
    //
    // ⚠️ Ant Design pasa `filters` con TODAS las columnas que tienen
    // capacidad de filtro, con `null` cuando esa columna no está
    // filtrando (`{producer: null, farm: ['Foo'], town: null, ...}`).
    // Comparar `JSON.stringify(filters)` con la URL (que sólo guarda
    // filtros activos) daba SIEMPRE distinto y forzaba
    // `filtersChanged=true` en cada click de la paginación — la
    // página volvía a 1 y la tabla no cambiaba. Normalizamos ambos
    // lados a "sólo filtros con array no vacío + valores ordenados"
    // antes de comparar.
    const normalize = (obj: Record<string, any>): Record<string, string[]> => {
      const out: Record<string, string[]> = {};
      Object.entries(obj || {}).forEach(([k, v]) => {
        if (Array.isArray(v) && v.length > 0) {
          out[k] = v.map(String).sort();
        }
      });
      return out;
    };
    const prevFiltersFromUrl: Record<string, string[]> = (() => {
      const acc: Record<string, string[]> = {};
      searchParams.forEach((v, k) => {
        if (k.startsWith("f_") && v) {
          acc[k.slice(2)] = v.split(",");
        }
      });
      return acc;
    })();
    const filtersChanged =
      JSON.stringify(normalize(filters)) !==
      JSON.stringify(normalize(prevFiltersFromUrl));

    const nextPage = filtersChanged ? 1 : Number(pagination?.current ?? auditCurrentPage);
    const nextPageSize = Number(pagination?.pageSize ?? auditPageSize);
    setAuditCurrentPage(nextPage);
    setAuditPageSize(nextPageSize);

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        // Filtros: limpio previos y vuelco los nuevos.
        Array.from(next.keys())
          .filter((k) => k.startsWith("f_"))
          .forEach((k) => next.delete(k));
        Object.entries(filters || {}).forEach(([col, vals]) => {
          if (Array.isArray(vals) && vals.length > 0) {
            next.set(`f_${col}`, vals.map(String).join(","));
          }
        });
        // Sorter (sólo uno a la vez).
        const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        if (singleSorter?.field && singleSorter?.order) {
          const order = singleSorter.order === "ascend" ? "asc" : "desc";
          next.set("sort", `${String(singleSorter.field)}:${order}`);
        } else {
          next.delete("sort");
        }
        // Paginación — sincronizamos SIEMPRE porque puede ser el
        // único cambio (usuario pinchó la página) y porque un cambio
        // de filtro fuerza el reset a página 1.
        next.set("auditPage", String(nextPage));
        next.set("auditPageSize", String(nextPageSize));
        // Legacy params sin prefijo — limpieza defensiva.
        next.delete("page");
        next.delete("pageSize");
        return next;
      },
      { replace: true },
    );
  };

  // ----- Comments drawer -----------------------------------------------
  // Se abre desde el botón "Comentarios" del header. Deep-link admitido
  // vía `?tab=comments&ticket=<id>` (email → volver directamente al
  // hilo concreto). Al cerrar limpiamos los params para no reabrir en
  // un refresh.
  const [commentsOpen, setCommentsOpen] = useState<boolean>(
    searchParams.get("tab") === "comments",
  );
  const commentsInitialTicketId = (() => {
    const raw = searchParams.get("ticket");
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const openComments = () => {
    setCommentsOpen(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "comments");
        return next;
      },
      { replace: true },
    );
  };
  const closeComments = () => {
    setCommentsOpen(false);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        next.delete("ticket");
        return next;
      },
      { replace: true },
    );
  };

  // Force regular auditors (user_type=5) to only see their own audits; the
  // dropdown isn't rendered for them, so we set the filter once we know
  // their app_user.id.
  useEffect(() => {
    if (isAuditorOnly && myAppUserId != null) {
      setAuditorFilter(myAppUserId);
    }
  }, [isAuditorOnly, myAppUserId]);

  // The slice of audits we display. The auditor filter affects BOTH the
  // table and the stats so a coordinator can pick "Mónica" and see her
  // numbers without further clicks.
  const scopedAudits = useMemo<Audit[]>(() => {
    if (auditorFilter === "all") return allAudits;
    if (auditorFilter === "unassigned") {
      return allAudits.filter((a: any) => a.auditor_user == null);
    }
    return allAudits.filter(
      (a: any) => a.auditor_user === auditorFilter,
    );
  }, [allAudits, auditorFilter]);

  // List of auditors with at least one audit on this planning — drives
  // the filter dropdown options (no extra fetch needed).
  const auditorOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const a of allAudits as any[]) {
      if (a.auditor_user != null && !seen.has(a.auditor_user)) {
        seen.set(a.auditor_user, a.auditor_name || `#${a.auditor_user}`);
      }
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allAudits]);

  // Filter options for the per-column header dropdowns. Antd expects
  // `{text, value}` entries. We derive them from `allAudits` so the
  // dropdowns offer every value present on the planning, irrespective
  // of the auditor scoping above.
  const buildColumnFilters = (key: string) =>
    Array.from(
      new Set(
        (allAudits as any[])
          .map((a) => a[key])
          .filter((v) => v != null && v !== "")
          .map((v) => String(v)),
      ),
    )
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ text: v, value: v }));

  const producerFilterOpts = useMemo(
    () => buildColumnFilters("producer_name"),
    [allAudits],
  );
  const farmFilterOpts = useMemo(
    () => buildColumnFilters("farm_name"),
    [allAudits],
  );
  const provinceFilterOpts = useMemo(
    () => buildColumnFilters("province_name"),
    [allAudits],
  );
  const townFilterOpts = useMemo(
    () => buildColumnFilters("town_name"),
    [allAudits],
  );
  const auditTypeFilterOpts = useMemo(
    () => buildColumnFilters("audit_type_name"),
    [allAudits],
  );
  // State filter uses the derived display label so the dropdown
  // matches what the cell renders (incl. "Sin asignar" / "Asignada").
  const stateFilterOpts = useMemo(() => {
    const seen = new Set<string>();
    for (const a of allAudits) {
      const display = getDisplayState(a);
      if (display) seen.add(display);
    }
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ text: v, value: v }));
  }, [allAudits]);
  // Auditor filter for the column header — every assigned auditor on
  // the planning plus a synthetic "__unassigned__" entry for null rows.
  const auditorFilterOpts = useMemo(() => {
    const seen = new Map<string, string>();
    let hasUnassigned = false;
    for (const a of allAudits as any[]) {
      if (a.auditor_user == null) {
        hasUnassigned = true;
        continue;
      }
      const k = String(a.auditor_user);
      if (!seen.has(k)) {
        seen.set(k, a.auditor_name || `#${a.auditor_user}`);
      }
    }
    const entries = Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ text: label, value }));
    if (hasUnassigned) {
      entries.unshift({ text: "Sin asignar", value: "__unassigned__" });
    }
    return entries;
  }, [allAudits]);
  const licenseContactNameFilterOpts = useMemo(
    () => buildColumnFilters("license_contact_name"),
    [allAudits],
  );
  const licenseContactPhoneFilterOpts = useMemo(
    () => buildColumnFilters("license_contact_phone"),
    [allAudits],
  );
  const licenseContactEmailFilterOpts = useMemo(
    () => buildColumnFilters("license_contact_email"),
    [allAudits],
  );
  const farmContactNameFilterOpts = useMemo(
    () => buildColumnFilters("farm_contact_name"),
    [allAudits],
  );
  const farmContactPhoneFilterOpts = useMemo(
    () => buildColumnFilters("farm_contact_phone"),
    [allAudits],
  );
  const farmContactEmailFilterOpts = useMemo(
    () => buildColumnFilters("farm_contact_email"),
    [allAudits],
  );

  // Role-scoped progress stats — recomputed every time the filter or the
  // raw audit list changes.
  const stats = useMemo(() => {
    const list = scopedAudits as any[];
    const total = list.length;
    // An audit "closes" the pending bucket when it has been
    // started OR explicitly cancelled — both outcomes mean the
    // coordinator no longer has work waiting on it. Same rule
    // applies to the audited-surface aggregate.
    const isClosed = (a: any) =>
      a.start_date != null || a.status_of_audit === 'canceled';
    const realized = list.filter(isClosed).length;
    const pending = total - realized;
    const unassigned = list.filter((a) => a.auditor_user == null).length;
    const totalSurface = list.reduce(
      (s, a) => s + Number(a.farm_surface_tango ?? a.surface ?? 0), 0,
    );
    const realizedSurface = list
      .filter(isClosed)
      .reduce((s, a) => s + Number(a.farm_surface_tango ?? a.surface ?? 0), 0);
    const samples = list.reduce((s, a) => s + Number(a.samples_taken_count ?? 0), 0);
    const controlPoints = list.reduce(
      (s, a) => s + Number(a.control_points_count ?? 0), 0,
    );
    const pctCount = total > 0 ? (realized / total) * 100 : 0;
    const pctSurface = totalSurface > 0 ? (realizedSurface / totalSurface) * 100 : 0;
    return {
      total, realized, pending, unassigned,
      totalSurface, realizedSurface,
      pctCount, pctSurface,
      samples, controlPoints,
    };
  }, [scopedAudits]);

  // Load all audits when planning group is available
  useEffect(() => {
    const loadAllAudits = async () => {
      if (!record?.id) return;
      
      setLoadingAudits(true);
      try {
        const { apiClient } = await import('../../services/api/apiClient');
        // Request with a large page_size to get all audits
        const response = await apiClient.get(`/audits/?planning_group=${record.id}&page_size=10000`);
        
        if (response.data.results) {
          setAllAudits(response.data.results);
        } else if (Array.isArray(response.data)) {
          setAllAudits(response.data);
        }
      } catch (error) {
        message.error('Error al cargar las auditorías');
      } finally {
        setLoadingAudits(false);
      }
    };

    loadAllAudits();
  }, [record?.id]);

  // Permanent filter: planning, plus auditor scoping when the current
  // user is a regular auditor (type=5). Coordinators and admins see all
  // routes/waypoints of the planning; auditors only their own. The
  // backend filtersets accept `auditor` as a query param.
  const routeWaypointPermanent: Array<{
    field: string;
    operator: "eq";
    value: number;
  }> = [];
  if (record?.id) {
    routeWaypointPermanent.push({
      field: "planning",
      operator: "eq",
      value: record.id,
    });
  }
  if (isAuditorOnly && myAppUserId != null) {
    routeWaypointPermanent.push({
      field: "auditor",
      operator: "eq",
      value: myAppUserId,
    });
  }

  // Routes / Waypoints tables — traemos TODO el dataset del planning
  // (pageSize alto) para que el filtro por texto, el sort y la
  // paginación local operen sobre el conjunto completo. Antes usábamos
  // `pageSize: 10` server-side y la paginación local sólo veía esas 10
  // filas: al subir la ruta 51 (planning 122 con 53 rutas) la nueva
  // caía fuera de la primera página server-side y "desaparecía" para
  // el usuario aunque estuviera en BD.
  const { tableProps: routeTableProps } = useTable<Route>({
    resource: "routes",
    filters: {
      permanent: routeWaypointPermanent,
    },
    syncWithLocation: false,
    pagination: {
      pageSize: 10000,
    },
    queryOptions: {
      enabled: !!record?.id && (!isAuditorOnly || myAppUserId != null),
    },
  });

  const { tableProps: waypointTableProps } = useTable({
    resource: "waypoints",
    filters: {
      permanent: routeWaypointPermanent,
    },
    syncWithLocation: false,
    pagination: {
      pageSize: 10000,
    },
    queryOptions: {
      enabled: !!record?.id && (!isAuditorOnly || myAppUserId != null),
    },
  });

  // Delete audit mutation
  const { mutate: deleteAudit } = useDelete();

  // Delete route mutation
  const { mutate: deleteRoute } = useDelete();

  // Delete waypoint mutation
  const { mutate: deleteWaypoint } = useDelete();

  const handleDeleteAudit = (auditId: number) => {
    deleteAudit(
      {
        resource: "audits",
        id: auditId,
      },
      {
        onSuccess: () => {
          message.success(t("audit.delete_success") || "Audit deleted successfully");
          reloadAudits();
          // Refetch planning group to update total_surface
          query.refetch();
        },
        onError: () => {
          message.error(t("audit.delete_error") || "Failed to delete audit");
        },
      }
    );
  };

  const handleEditAudit = (audit: Audit) => {
    setSelectedAudit(audit);
    setEditModalVisible(true);
  };

  const reloadAudits = async () => {
    if (!record?.id) return;
    
    setLoadingAudits(true);
    try {
      const { apiClient } = await import('../../services/api/apiClient');
      const response = await apiClient.get(`/audits/?planning_group=${record.id}&page_size=10000`);
      
      if (response.data.results) {
        setAllAudits(response.data.results);
      } else if (Array.isArray(response.data)) {
        setAllAudits(response.data);
      }
    } catch (error) {
      // Error handled silently or with message
    } finally {
      setLoadingAudits(false);
    }
  };

  // Refresco "defensivo" cuando el navegador restaura la página
  // desde su BFCache (Atrás/Adelante con la página congelada en
  // memoria). El caso normal — navegación SPA con React Router —
  // ya remonta el componente y dispara los useEffect; este listener
  // solo cubre el edge case en que el browser sirve el DOM cacheado
  // y React no se entera del cambio.
  useEffect(() => {
    const onPageshow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        reloadAudits();
        query.refetch();
      }
    };
    window.addEventListener("pageshow", onPageshow);
    return () => window.removeEventListener("pageshow", onPageshow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando la pestaña vuelve al foco (usuario cambia de tab, o
  // vuelve al navegador tras usar otra app). Refine puede estar
  // sirviendo el `record` desde cache y `allAudits` puede haberse
  // quedado desactualizado si otro admin/auditor jefe modificó
  // auditorías fuera de esta pestaña. Cubre también el caso reportado
  // en el detalle del planning: el user abre una auditoría, cambia
  // su estado y vuelve — los contadores del filtro reflejan el
  // cambio en lugar de quedarse con el snapshot anterior.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible" && record?.id) {
        reloadAudits();
        query.refetch();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  // Navegación SPA: refrescamos cuando el usuario aterriza en esta
  // pantalla desde otra ruta (típicamente al volver de /audits/{id})
  // para que los contadores del filtro reflejen cambios hechos allí.
  //
  // ⚠️  ANTES: dep `location.key`. Cambiar de página / filtro también
  // muta `location.key` porque `setSearchParams({replace:true})` genera
  // una entrada nueva del history. Consecuencia: cada click en la
  // paginación disparaba `reloadAudits()`, el dataSource se
  // reinicializaba y la paginación volvía a la 1 mostrando siempre
  // los mismos registros. Con `location.pathname` sólo disparamos
  // cuando el path cambia de verdad — los query params (paginación,
  // filtros, sort, tabs) ya no reinician la tabla.
  useEffect(() => {
    if (record?.id) {
      reloadAudits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleCreateSuccess = () => {
    reloadAudits();
    // Refetch planning group to update total_surface
    query.refetch();
  };

  const handleUpdateSuccess = () => {
    reloadAudits();
    // Refetch planning group to update total_surface
    query.refetch();
  };

  const handleViewMap = () => {
    if (record?.id) {
      // Navigate to map page using React Router
      navigate(`/planning-groups/show/${record.id}/map`);
    }
  };

  const handleRouteUploadSuccess = () => {
    // Invalidate routes list to trigger refetch
    invalidate({
      resource: "routes",
      invalidates: ["list"],
    });
  };

  const handleWaypointUploadSuccess = () => {
    // Invalidate waypoints list to trigger refetch
    invalidate({
      resource: "waypoints",
      invalidates: ["list"],
    });
  };

  const handleDeleteRoute = (routeId: number) => {
    deleteRoute(
      {
        resource: "routes",
        id: routeId,
      },
      {
        onSuccess: () => {
          message.success(t("route.delete_success") || "Route deleted successfully");
          // Invalidate routes list to trigger refetch
          invalidate({
            resource: "routes",
            invalidates: ["list"],
          });
        },
        onError: () => {
          message.error(t("route.delete_error") || "Failed to delete route");
        },
      }
    );
  };

  const handleDownloadGPX = (route: Route) => {
    if (route.gpx_file_url) {
      window.open(route.gpx_file_url, "_blank");
    } else {
      message.error(t("route.no_gpx_file") || "No GPX file available");
    }
  };

  const handleDeleteWaypoint = (waypointId: number) => {
    deleteWaypoint(
      {
        resource: "waypoints",
        id: waypointId,
      },
      {
        onSuccess: () => {
          message.success(t("waypoint.delete_success") || "Waypoint file deleted successfully");
          // Invalidate waypoints list to trigger refetch
          invalidate({
            resource: "waypoints",
            invalidates: ["list"],
          });
        },
        onError: () => {
          message.error(t("waypoint.delete_error") || "Failed to delete waypoint file");
        },
      }
    );
  };

  const handleDownloadWaypointGPX = (waypoint: any) => {
    if (waypoint.gpx_file_url) {
      window.open(waypoint.gpx_file_url, "_blank");
    } else {
      message.error(t("waypoint.no_gpx_file") || "No GPX file available");
    }
  };

  const handleViewWaypointDetail = (waypointId: number) => {
    setSelectedWaypointId(waypointId);
    setWaypointDetailModalVisible(true);
  };

  /**
   * Dispara POST /waypoints/{id}/relookup-samples/ para reprocesar
   * las muestras del planning que aún no tenían geo_point y ahora
   * pueden matchear un nombre de este archivo GPX. Útil cuando el
   * auditor sube el waypoint DESPUÉS de haber registrado las muestras
   * de punto de control — antes no había forma bulk de re-vincular.
   */
  const handleRelookupWaypointSamples = async (waypointId: number, code?: string) => {
    setRelookupingWpId(waypointId);
    try {
      const { apiClient } = await import("../../services/api/apiClient");
      const resp = await apiClient.post<{
        scanned: number;
        updated: number;
        unchanged: number;
      }>(`/waypoints/${waypointId}/relookup-samples/`);
      const { scanned, updated, unchanged } = resp.data;
      const label = code || waypointId;
      if (updated > 0) {
        appModal.success({
          title: t("waypoint.relookup_result_title", "Waypoints reprocesados"),
          content: t(
            "waypoint.relookup_success",
            `Waypoint ${label}: ${updated} muestra(s) actualizadas con geometría (de ${scanned} revisadas).`,
          ),
        });
        reloadAudits();
      } else {
        appModal.info({
          title: t("waypoint.relookup_result_title", "Waypoints reprocesados"),
          content: t(
            "waypoint.relookup_no_matches",
            `Waypoint ${label}: revisadas ${scanned} muestras pendientes; ninguna casa con este archivo.`,
          ),
        });
      }
      // Silencia el `unchanged` — es implícito.
      void unchanged;
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ?? err?.message ?? "Error";
      appModal.error({
        title: t("waypoint.relookup_error_title", "Error al reprocesar"),
        content: t(
          "waypoint.relookup_error",
          `No se pudo reprocesar el waypoint: ${detail}`,
        ),
      });
    } finally {
      setRelookupingWpId(null);
    }
  };

  const handleAssignAuditor = (audit: Audit) => {
    setAuditToAssign(audit);
    setAssignAuditorModalVisible(true);
  };

  // Nota: los botones "Regenerar parcelas" (precharge AuditPlots ←
  // PlotReals) y "Materializar canónicas" (PlotReals ← AuditPlots)
  // se quitaron de la UI. La materialización del histórico canónico
  // se ejecuta ahora desde consola con
  //   `python manage.py materialize_canonical_plots --planning-group <id>`.
  // El endpoint backend `/precharge-audit-plots/` sigue disponible
  // para scripts/herramientas admin si hace falta.
  const handleAssignAuditorSuccess = () => {
    reloadAudits();
    // Refetch planning group
    query.refetch();
  };

  // Download the full audit table as .xlsx — the coordinator's
  // assignment toolbox. Hits the backend's export endpoint, which
  // intentionally ignores the UI filters and returns every audit in
  // this planning so the spreadsheet is canonical and shareable.
  const handleExportAudits = async () => {
    if (!idParam) {
      appMessageCtx.error(
        t(
          "planning.export_audits_missing_id",
          "Falta el id de la planificación, recarga la página.",
        ),
      );
      return;
    }
    setExportingAudits(true);
    try {
      const { apiClient } = await import("../../services/api/apiClient");
      const response = await apiClient.get<Blob>(
        `/planning-groups/${idParam}/export-audits/`,
        { responseType: "blob" },
      );
      // Pull the suggested filename from Content-Disposition so the
      // browser preserves the backend's naming convention; fall back
      // to a sensible default if the header isn't readable.
      const cd = (response.headers as any)["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(cd);
      const filename = match?.[1] || "auditorias.xlsx";

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      appMessageCtx.error(
        e?.response?.data?.detail ||
          e?.message ||
          t(
            "planning.export_audits_error",
            "No se pudo exportar el Excel.",
          ),
      );
    } finally {
      setExportingAudits(false);
    }
  };

  // ---- planning_date modal --------------------------------------------
  // Auditors can pick the day they're planning to visit each audit. We
  // keep it as a tiny dedicated modal (single DatePicker) so the action
  // is one click + one date, no auditor/state distractions.
  const [planningDateModalVisible, setPlanningDateModalVisible] = useState(false);
  const [auditForPlanningDate, setAuditForPlanningDate] = useState<Audit | null>(null);
  const [planningDateValue, setPlanningDateValue] = useState<Dayjs | null>(null);
  const [savingPlanningDate, setSavingPlanningDate] = useState(false);

  const openPlanningDateModal = (audit: Audit) => {
    setAuditForPlanningDate(audit);
    setPlanningDateValue(
      audit.planning_date ? dayjs(audit.planning_date) : null,
    );
    setPlanningDateModalVisible(true);
  };

  const submitPlanningDate = async () => {
    if (!auditForPlanningDate) return;
    setSavingPlanningDate(true);
    try {
      const { apiClient } = await import("../../services/api/apiClient");
      await apiClient.patch(`/audits/${auditForPlanningDate.id}/`, {
        planning_date: planningDateValue
          ? planningDateValue.format("YYYY-MM-DD")
          : null,
      });
      message.success(
        t(
          "audit.planning_date_saved",
          "Fecha de planificación actualizada",
        ),
      );
      setPlanningDateModalVisible(false);
      setAuditForPlanningDate(null);
      reloadAudits();
      query.refetch();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ||
        t(
          "audit.planning_date_error",
          "No se pudo guardar la fecha de planificación",
        );
      message.error(msg);
    } finally {
      setSavingPlanningDate(false);
    }
  };

  const handleCancelAudit = (audit: Audit) => {
    setAuditToCancel(audit);
    setCancelAuditModalVisible(true);
  };

  const handleCancelAuditSuccess = () => {
    reloadAudits();
    query.refetch();
  };

  // Per-row data-bundle (Excel + map PDF) download. Tracks the row
  // being processed so we only show a spinner on the right button.
  const [downloadingBundleId, setDownloadingBundleId] = useState<number | null>(null);
  const handleDownloadBundle = async (audit: Audit) => {
    setDownloadingBundleId(audit.id);
    const hide = message.loading(
      t("audit.bundle_loading", "Generando paquete (Excel + PDF mapa)..."),
      0,
    );
    try {
      await downloadAuditBundle(audit.id, { reference: audit.reference });
      message.success(
        t("audit.bundle_success", "Paquete descargado correctamente"),
      );
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        t("audit.bundle_error", "No se pudo generar el paquete");
      message.error(detail);
    } finally {
      hide();
      setDownloadingBundleId(null);
    }
  };

  return (
    <Show
      isLoading={isLoading}
      // Desactivamos la flecha "back" que Refine renderiza junto al
      // título — teníamos duplicidad con el botón "Volver" del header
      // buttons, que además decide el listPath en función de si es
      // admin o auditor. Nos quedamos sólo con el explícito.
      goBack={null}
      title={record?.name || t("planning.title", "Planificación")}
      contentProps={{
        style: {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
      headerButtons={({ editButtonProps }) => {
        // The same component is mounted under two routes:
        //  - /planning-groups/show/:id          (admin)
        //  - /auditor/planning-groups/:id       (auditor/coordinator)
        // Refine's <ListButton> always navigates to the resource's
        // registered list path, which sends auditors to the admin list
        // (and 403s them). Pick the correct list based on the URL.
        const listPath = location.pathname.startsWith("/auditor/")
          ? "/auditor/planning-groups"
          : "/planning-groups";
        return (
          <>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(listPath)}
            >
              {t("common.back") || "Volver"}
            </Button>
            <Button
              type="default"
              icon={<EnvironmentOutlined />}
              onClick={handleViewMap}
            >
              {t("planning.view_map") || "Ver Mapa"}
            </Button>
            <Button
              type="default"
              icon={<CommentOutlined />}
              onClick={openComments}
            >
              {t("planning.comments.button", "Comentarios")}
            </Button>
            {isAdmin && (
              <>
                <EditButton {...editButtonProps}>
                  {t("common.edit") || "Editar"}
                </EditButton>
                <DeleteButton />
              </>
            )}
          </>
        );
      }}
    >
      {/* Metadatos compactos justo bajo el título: campaña + empresa
          auditora como tags. Sustituye a la antigua card de
          "Información Básica". */}
      <Space size={[8, 8]} wrap style={{ marginBottom: 16 }}>
        {record?.season?.name && (
          <Tag
            icon={<CalendarOutlined />}
            color="cyan"
            style={{ padding: "2px 10px", fontSize: 13 }}
          >
            {record.season.name}
          </Tag>
        )}
        {record?.audit_company_name ? (
          <Tag
            icon={<ApartmentOutlined />}
            color="purple"
            style={{ padding: "2px 10px", fontSize: 13 }}
          >
            {record.audit_company_name}
          </Tag>
        ) : (
          <Tag
            icon={<ApartmentOutlined />}
            style={{ padding: "2px 10px", fontSize: 13 }}
          >
            {t("planning.audit_company_unassigned", "Empresa auditora sin asignar")}
          </Tag>
        )}
      </Space>

      {/* Main Statistics Section — role-scoped via auditorFilter */}
      <Card
        title={t("planning.audit_summary")}
        bordered={false}
        style={{ marginBottom: 24 }}
        extra={
          canPickAuditor && auditorOptions.length > 0 ? (
            <Space>
              <span style={{ fontSize: 13, color: "#8c8c8c" }}>
                {t("audit.auditor", "Auditor")}:
              </span>
              <Select<number | "all" | "unassigned">
                style={{ minWidth: 220 }}
                value={auditorFilter}
                onChange={(v) => setAuditorFilter(v)}
                showSearch
                optionFilterProp="label"
                options={[
                  { value: "all", label: t("planning.all_auditors", "Todos los auditores") },
                  {
                    value: "unassigned",
                    label: t("planning.unassigned_auditor", "Sin asignar"),
                  },
                  ...auditorOptions,
                ]}
              />
            </Space>
          ) : null
        }
      >
          {/* Cabecera de números — la fila que ocupa el espacio noble.
              Muestra los totales que interesan de un vistazo: productores
              (planificados/auditados), auditorías (con chips por tipo),
              proyección total, kg auditor, kg productor, muestras y CP.
              La proyección y kg finalizados usan los mismos campos que
              el detalle expandible; aquí los repetimos comprimidos para
              no obligar a abrir el Collapse. */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Statistic
                title={t("planning.producers_kpi", "Productores")}
                value={`${record?.audited_producers_count ?? 0} / ${record?.producers_count ?? 0}`}
                prefix={<UserOutlined />}
                valueStyle={{ color: "#1677ff", fontSize: 22 }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t(
                  "planning.producers_kpi_hint",
                  "auditados / planificados",
                )}
              </Text>
            </Col>
            <Col xs={24} sm={12} md={8} lg={5}>
              <Statistic
                title={t("planning.audits_kpi", "Auditorías")}
                value={stats.total}
                prefix={<AuditOutlined />}
                valueStyle={{ color: "#722ed1", fontSize: 22 }}
              />
              <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
                <Tag color="green" style={{ margin: 0 }}>
                  {t("planning.audits_kpi_completed", "Cerradas")}: {stats.realized}
                </Tag>
                {isAdmin ? (
                  <Tag color="orange" style={{ margin: 0 }}>
                    {t("planning.audits_kpi_unassigned", "Sin asignar")}: {stats.unassigned}
                  </Tag>
                ) : (
                  <Tag color="gold" style={{ margin: 0 }}>
                    {t("planning.audits_kpi_pending", "Pendientes")}: {stats.pending}
                  </Tag>
                )}
              </div>
            </Col>
            <Col xs={24} sm={12} md={8} lg={5}>
              <Statistic
                title={t("planning.kpi_projected_total_short", "Proyección total")}
                value={record?.projected_total_kg ?? 0}
                suffix="kg"
                precision={0}
                valueStyle={{ color: "#fa8c16", fontSize: 22 }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t("planning.kpi_coverage_short", "Cobertura")}:{" "}
                {Number(record?.coverage_pct ?? 0).toFixed(0)}%
              </Text>
            </Col>
            <Col xs={24} sm={12} md={8} lg={5}>
              <Statistic
                title={t("planning.kpi_kg_auditor_short", "Kg auditor")}
                value={record?.finalized_kg_auditor ?? 0}
                suffix="kg"
                precision={0}
                valueStyle={{ color: "#52c41a", fontSize: 22 }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t("planning.kpi_kg_finalized_hint", "Finalizadas + canceladas")}
              </Text>
            </Col>
            <Col xs={24} sm={12} md={8} lg={5}>
              <Statistic
                title={t("planning.kpi_kg_producer_short", "Kg productor")}
                value={record?.finalized_kg_producer ?? 0}
                suffix="kg"
                precision={0}
                valueStyle={{ color: "#1890ff", fontSize: 22 }}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {t("planning.kpi_kg_finalized_hint", "Finalizadas + canceladas")}
              </Text>
            </Col>
          </Row>

          {/* Barras compactas de % completado — el foco visual antes.
              Ahora en línea, con altura mínima para que ocupen poco.
              Muestras + CP se convierten en dos chips a la derecha
              porque son secundarios respecto al progreso. */}
          <Row gutter={[16, 8]} style={{ marginTop: 16 }} align="middle">
            <Col xs={24} md={16}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: "rgba(0,0,0,.45)", minWidth: 68 }}>
                  {t("planning.by_count", "Por nº")}
                </span>
                <Progress
                  percent={Math.round(stats.pctCount * 10) / 10}
                  strokeColor="#52c41a"
                  size={{ height: 8 } as any}
                  style={{ flex: 1, margin: 0 }}
                />
                <span style={{ fontSize: 11, color: "rgba(0,0,0,.65)", minWidth: 70, textAlign: "right" }}>
                  {stats.realized} / {stats.total}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12, color: "rgba(0,0,0,.45)", minWidth: 68 }}>
                  {t("planning.by_surface", "Por sup.")}
                </span>
                <Progress
                  percent={Math.round(stats.pctSurface * 10) / 10}
                  strokeColor="#13c2c2"
                  size={{ height: 8 } as any}
                  style={{ flex: 1, margin: 0 }}
                />
                <span style={{ fontSize: 11, color: "rgba(0,0,0,.65)", minWidth: 90, textAlign: "right" }}>
                  {stats.realizedSurface.toFixed(0)} / {stats.totalSurface.toFixed(0)} ha
                </span>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <Space size={8} wrap>
                <Tag icon={<ExperimentOutlined />} color="purple" style={{ margin: 0 }}>
                  {t("planning.samples_taken", "Muestras")}: {stats.samples}
                </Tag>
                <Tag icon={<EnvironmentOutlined />} color="orange" style={{ margin: 0 }}>
                  {t("planning.control_points_taken", "Puntos de control")}: {stats.controlPoints}
                </Tag>
              </Space>
            </Col>
          </Row>

          {/* Admin-only deep dive — audit type breakdown + production
              estimates. Hidden behind a collapse so it doesn't drown the
              essentials. Coordinators/auditors don't need this on the
              planning detail. */}
          {isAdmin && (
            <Collapse
              ghost
              style={{ marginTop: 12 }}
              items={[
                {
                  key: "admin-detail",
                  label: t("planning.admin_detail_label", "Detalle avanzado (tipos + producción)"),
                  children: (
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title={t("planning.complete_audits") || "Auditorías Completas"}
                            value={record?.complete_audits ?? 0}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: "#52c41a" }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title={t("planning.production_audits") || "Auditorías Aforo"}
                            value={record?.production_audits ?? 0}
                            prefix={<BarChartOutlined />}
                            valueStyle={{ color: "#722ed1" }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title={t("planning.localization_audits") || "Auditorías Localización"}
                            value={record?.localization_audits ?? 0}
                            prefix={<EnvironmentOutlined />}
                            valueStyle={{ color: "#fa8c16" }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title={t("planning.sampling_audits") || "Auditorías Toma de Muestras"}
                            value={record?.sampling_audits ?? 0}
                            prefix={<AuditOutlined />}
                            valueStyle={{ color: "#13c2c2" }}
                          />
                        </Card>
                      </Col>

                      {/* --- Aforo real (sólo auditorías finalizadas) --- */}
                      <Col xs={24}>
                        <Card
                          size="small"
                          title={
                            <Space size="small">
                              <CheckCircleOutlined style={{ color: "#52c41a" }} />
                              <strong>
                                {t(
                                  "planning.kpi_real_title",
                                  "Aforo consolidado — auditorías finalizadas",
                                )}
                              </strong>
                              <Tag color="green">
                                {record?.finalized_audits_count ?? 0} / {record?.total_audits ?? 0}
                              </Tag>
                            </Space>
                          }
                          extra={
                            <Tooltip
                              title={t(
                                "planning.kpi_real_tooltip",
                                "Sólo auditorías con estado 'Finalizada' — filtra las iniciadas sin kg reportados que antes distorsionaban la media.",
                              )}
                            >
                              <Text type="secondary" style={{ fontSize: 12, cursor: "help" }}>
                                <InfoCircleOutlined /> {t("common.how_calculated", "Cómo se calcula")}
                              </Text>
                            </Tooltip>
                          }
                        >
                          <Row gutter={[16, 16]}>
                            {/* Fila 1 — kilos declarados por cada actor */}
                            <Col xs={24} sm={12}>
                              <Statistic
                                title={t("planning.kpi_kg_auditor", "Kg estimados por el auditor")}
                                value={record?.finalized_kg_auditor ?? 0}
                                suffix="kg"
                                precision={0}
                                valueStyle={{ color: "#52c41a" }}
                              />
                            </Col>
                            <Col xs={24} sm={12}>
                              <Statistic
                                title={t("planning.kpi_kg_producer", "Kg estimados por el productor")}
                                value={record?.finalized_kg_producer ?? 0}
                                suffix="kg"
                                precision={0}
                                valueStyle={{ color: "#1890ff" }}
                              />
                            </Col>

                            {/* Fila 2 — superficies (licenciada / auditada / productiva) + kg/ha */}
                            <Col xs={12} sm={6}>
                              <Statistic
                                title={
                                  <Tooltip
                                    title={t(
                                      "planning.kpi_surface_licensed_tooltip",
                                      "Superficie asignada a la finca en el maestro Tango (Farm.surface_tango). Es el cupo nominal declarado.",
                                    )}
                                  >
                                    <span style={{ cursor: "help" }}>
                                      {t("planning.kpi_surface_licensed", "Superficie licenciada")}{" "}
                                      <InfoCircleOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
                                    </span>
                                  </Tooltip>
                                }
                                value={record?.finalized_surface ?? 0}
                                suffix="ha"
                                precision={2}
                                valueStyle={{ color: "#13c2c2" }}
                              />
                            </Col>
                            <Col xs={12} sm={6}>
                              <Statistic
                                title={
                                  <Tooltip
                                    title={t(
                                      "planning.kpi_surface_audited_tooltip",
                                      "Suma de la superficie de las parcelas auditadas (AuditPlot.surface), incluyendo parcelas que el auditor midió pero declaró sin producción.",
                                    )}
                                  >
                                    <span style={{ cursor: "help" }}>
                                      {t("planning.kpi_surface_audited", "Superficie auditada")}{" "}
                                      <InfoCircleOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
                                    </span>
                                  </Tooltip>
                                }
                                value={record?.finalized_surface_audited ?? 0}
                                suffix="ha"
                                precision={2}
                                valueStyle={{ color: "#2f54eb" }}
                              />
                            </Col>
                            <Col xs={12} sm={6}>
                              <Statistic
                                title={
                                  <Tooltip
                                    title={t(
                                      "planning.kpi_surface_productive_tooltip",
                                      "Superficie de las parcelas auditadas que aportan producción (AuditPlot.surface con kg_plot > 0). Descarta las parcelas sin producción reportada.",
                                    )}
                                  >
                                    <span style={{ cursor: "help" }}>
                                      {t("planning.kpi_surface_productive", "Superficie productiva")}{" "}
                                      <InfoCircleOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
                                    </span>
                                  </Tooltip>
                                }
                                value={record?.finalized_surface_productive ?? 0}
                                suffix="ha"
                                precision={2}
                                valueStyle={{ color: "#722ed1" }}
                              />
                            </Col>
                            <Col xs={12} sm={6}>
                              <Statistic
                                title={
                                  <Tooltip
                                    title={t(
                                      "planning.kpi_kg_per_ha_productive_tooltip",
                                      "Kg del auditor ÷ superficie productiva. Rendimiento real por hectárea que aporta producción — no diluye la media con parcelas vacías o superficie licenciada no plantada.",
                                    )}
                                  >
                                    <span style={{ cursor: "help" }}>
                                      {t("planning.kpi_kg_per_ha_productive", "Kg / ha productiva")}{" "}
                                      <InfoCircleOutlined style={{ color: "#8c8c8c", fontSize: 12 }} />
                                    </span>
                                  </Tooltip>
                                }
                                value={record?.kg_per_ha_productive ?? 0}
                                suffix="kg/ha"
                                precision={0}
                                valueStyle={{ color: "#fa8c16" }}
                              />
                            </Col>
                          </Row>
                        </Card>
                      </Col>

                      {/* --- Proyección con indicador de fiabilidad --- */}
                      <Col xs={24}>
                        {(() => {
                          const reliability = (record as any)?.reliability_level as
                            | "high" | "medium" | "low" | "none" | undefined;
                          const coverage = Number(record?.coverage_pct ?? 0);
                          const totalProjected = Number(record?.projected_total_kg ?? 0);
                          const pendingKg = Number(record?.projected_pending_kg ?? 0);
                          const pendingSurface = Number(record?.pending_surface ?? 0);
                          // Paleta según fiabilidad — verde ≥75%, amarillo
                          // 40–75%, rojo <40%, gris cuando aún no hay datos.
                          const palette = {
                            high:   { color: "#52c41a", label: "Alta fiabilidad",  bg: "#f6ffed", stroke: "#52c41a" },
                            medium: { color: "#fa8c16", label: "Fiabilidad media", bg: "#fff7e6", stroke: "#fa8c16" },
                            low:    { color: "#f5222d", label: "Fiabilidad baja",  bg: "#fff1f0", stroke: "#f5222d" },
                            none:   { color: "#8c8c8c", label: "Sin datos aún",    bg: "#fafafa", stroke: "#d9d9d9" },
                          }[reliability ?? "none"];
                          return (
                            <Card
                              size="small"
                              style={{ backgroundColor: palette.bg }}
                              title={
                                <Space size="small">
                                  <BarChartOutlined style={{ color: palette.color }} />
                                  <strong>
                                    {t(
                                      "planning.kpi_projection_title",
                                      "Proyección total",
                                    )}
                                  </strong>
                                  <Tag color={
                                    reliability === "high" ? "green" :
                                    reliability === "medium" ? "orange" :
                                    reliability === "low" ? "red" : "default"
                                  }>
                                    {t(`planning.kpi_reliability_${reliability ?? "none"}`, palette.label)}
                                  </Tag>
                                </Space>
                              }
                              extra={
                                <Tooltip
                                  title={
                                    <div>
                                      <div><strong>Fórmula:</strong> kg finalizados + (media kg/ha finalizadas × superficie pendiente)</div>
                                      <div style={{ marginTop: 4 }}>
                                        <strong>Fiabilidad:</strong> verde ≥75% cobertura · amarillo 40–75% · rojo &lt;40%
                                      </div>
                                    </div>
                                  }
                                >
                                  <Text type="secondary" style={{ fontSize: 12, cursor: "help" }}>
                                    <InfoCircleOutlined /> {t("common.how_calculated", "Cómo se calcula")}
                                  </Text>
                                </Tooltip>
                              }
                            >
                              <Row gutter={[16, 8]} align="middle">
                                <Col xs={24} sm={10}>
                                  <Statistic
                                    title={t("planning.kpi_projected_total", "Kg totales proyectados")}
                                    value={totalProjected}
                                    suffix="kg"
                                    precision={0}
                                    valueStyle={{ color: palette.color, fontSize: 26 }}
                                  />
                                </Col>
                                <Col xs={24} sm={8}>
                                  <div style={{ marginBottom: 4 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      {t("planning.kpi_coverage", "Cobertura")}
                                    </Text>
                                  </div>
                                  <Progress
                                    percent={coverage}
                                    strokeColor={palette.stroke}
                                    format={(pct) => `${(pct ?? 0).toFixed(0)}%`}
                                    size="small"
                                  />
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    {(record?.finalized_surface ?? 0).toLocaleString("es-ES")} /{" "}
                                    {(record?.planned_surface ?? 0).toLocaleString("es-ES")} ha
                                  </Text>
                                </Col>
                                <Col xs={24} sm={6}>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {t("planning.kpi_pending", "Pendiente proyectado")}
                                  </Text>
                                  <div style={{ fontSize: 16, color: palette.color }}>
                                    {pendingKg.toLocaleString("es-ES")} kg
                                  </div>
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    ({pendingSurface.toLocaleString("es-ES")} ha)
                                  </Text>
                                </Col>
                              </Row>
                            </Card>
                          );
                        })()}
                      </Col>

                      {/* --- Gráfico: auditorías por auditor --- */}
                      <Col xs={24}>
                        <AuditsByAuditorChart
                          data={record?.audits_by_auditor ?? []}
                        />
                      </Col>
                    </Row>
                  ),
                },
              ]}
            />
          )}
        </Card>

      {/* Audits Table Section */}
      <Card
        title={t("planning.audits") || "Auditorías"}
        bordered={false}
        style={{ marginBottom: 24 }}
        extra={
            <Space wrap>
              {/* Top toolbar: global search + Limpiar filtros.
                  El filtro de estado está justo debajo del título como
                  `<Tabs>` para dar espacio a los 7 estados con contador
                  sin agobiar. El resto de filtros (productor, finca,
                  provincia, municipio, tipo, muestras, contactos,
                  auditor) viven en los column headers, mismo UX que
                  /auditor/assign. */}
              <Input.Search
                placeholder={t("common.search") || "Buscar..."}
                allowClear
                enterButton={<SearchOutlined />}
                size="middle"
                style={{ width: 300 }}
                value={auditSearchText}
                onChange={(e) => setAuditSearchText(e.target.value)}
              />
              {/* Toolbar auxiliar (Limpiar / Recargar / Asignar / Exportar)
                  oculta para admin — el admin trabaja el planning
                  desde la página maestra y no usa estos atajos aquí.
                  El auditor jefe (coordinator) tampoco necesita
                  Limpiar/Recargar/Asignar: trabaja sobre su lote fijo
                  y la asignación masiva se hace desde la barra flotante
                  que aparece al seleccionar filas. Solo el auditor
                  normal conserva los cuatro atajos. Exportar Excel sí
                  lo mantiene el coordinator. */}
              {!isAdmin && !isCoordinator && (
                <>
                  <Tooltip
                    title={t(
                      "coordinator.clear_filters",
                      "Limpiar filtros",
                    )}
                  >
                    <Button
                      icon={<ClearOutlined />}
                      onClick={() => {
                        setAuditSearchText("");
                        setAssignedFilter("all");
                        setTableResetCounter((n) => n + 1);
                      }}
                      aria-label={t(
                        "coordinator.clear_filters",
                        "Limpiar filtros",
                      )}
                    />
                  </Tooltip>
                  <Tooltip
                    title={t("planning.reload_audits", "Recargar auditorías")}
                  >
                    <Button
                      icon={<ReloadOutlined />}
                      loading={loadingAudits}
                      onClick={() => {
                        reloadAudits();
                        query.refetch();
                      }}
                      aria-label={t("planning.reload_audits", "Recargar auditorías")}
                    />
                  </Tooltip>
                  <Tooltip
                    title={t("planning.assign_audits", "Asignar auditorías")}
                  >
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() =>
                        navigate(`/planning-groups/show/${idParam}/assign-audits`)
                      }
                      aria-label={t(
                        "planning.assign_audits",
                        "Asignar auditorías",
                      )}
                    />
                  </Tooltip>
                </>
              )}
              {!isAdmin && (
                <Tooltip
                  title={t(
                    "planning.export_audits_tooltip",
                    "Exporta la tabla completa de auditorías de esta planificación a Excel",
                  )}
                >
                  <Button
                    icon={<DownloadOutlined />}
                    loading={exportingAudits}
                    onClick={handleExportAudits}
                    aria-label={t("planning.export_audits", "Exportar Excel")}
                  />
                </Tooltip>
              )}
              {isAdmin && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                >
                  {t("audit.create") || "Crear Auditoría"}
                </Button>
              )}
            </Space>
          }
        >
          {/* Toolbar de asignación masiva — visible solo para admin +
              auditor jefe cuando hay al menos una fila seleccionada
              en el Table de auditorías. */}
          {(isAdmin || isCoordinator) && selectedAuditIds.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
                padding: "8px 12px",
                background: "#e6f4ff",
                border: "1px solid #91caff",
                borderRadius: 6,
              }}
            >
              <Space>
                <strong>{selectedAuditIds.length}</strong>
                {t(
                  "planning.bulk_selected",
                  selectedAuditIds.length === 1
                    ? "auditoría seleccionada"
                    : "auditorías seleccionadas",
                )}
              </Space>
              <Space>
                <Button
                  type="primary"
                  onClick={() => setBulkAssignModalVisible(true)}
                >
                  {t("planning.bulk_assign_auditor", "Asignar auditor")}
                </Button>
                <Button onClick={() => setSelectedAuditIds([])}>
                  {t("common.clear_selection", "Limpiar selección")}
                </Button>
              </Space>
            </div>
          )}

          {/* Filtro por estado — `Tabs` con contador por bucket. Al
              elegir uno, la Table filtra por `matchesAssignedFilter`.
              Persistido en la URL vía `?state=…` para que el back del
              navegador restaure la vista. Un estado extra "Todas" al
              final sirve de reset rápido. */}
          {/* Segmented "Aforo enviado" — misma familia visual que el
              Tabs de estado. Muestra rápidamente qué audits ya están
              en Certification (fila en verde) y cuáles siguen sin
              enviar el aforo. `record.aforo_synced_at != null` es
              suficiente para clasificar. */}
          <Segmented
            size="small"
            value={aforoFilter}
            onChange={(v) => setAforoFilter(v as AforoFilter)}
            style={{ marginBottom: 12 }}
            options={(() => {
              const sentCount = scopedAudits.filter(
                (a: any) => Boolean(a?.aforo_synced_at),
              ).length;
              const pendingCount = scopedAudits.length - sentCount;
              return [
                {
                  label: `${t("planning.aforo_filter_all", "Todos aforos")} (${scopedAudits.length})`,
                  value: "all",
                },
                {
                  label: `${t("planning.aforo_filter_sent", "Aforo enviado")} (${sentCount})`,
                  value: "sent",
                },
                {
                  label: `${t("planning.aforo_filter_pending", "Aforo pendiente")} (${pendingCount})`,
                  value: "pending",
                },
              ];
            })()}
          />
          <Tabs
            activeKey={assignedFilter}
            onChange={(k) => setAssignedFilter(k as AssignedFilter)}
            size="small"
            style={{ marginBottom: 12 }}
            items={ASSIGNED_FILTER_ORDER.map((key) => {
              const count =
                key === "all"
                  ? scopedAudits.length
                  : scopedAudits.filter((a: any) =>
                      matchesAssignedFilter(a, key),
                    ).length;
              const labels: Record<AssignedFilter, string> = {
                unassigned: t(
                  "coordinator.filter_unassigned",
                  "Sin asignar",
                ),
                assigned: t("audit.state_asignada", "Asignada"),
                planificada: t("audit.state_planificada", "Planificada"),
                performed: t("audit.state_performed", "Realizada"),
                submitted: t(
                  "audit.state_submitted",
                  "Pendiente revisión",
                ),
                finalizada: t("audit.state_finalizada", "Finalizada"),
                cancelada: t("audit.state_cancelada", "Cancelada"),
                all: t("coordinator.filter_all", "Todas"),
              };
              return {
                key,
                label: (
                  <span>
                    {labels[key]}{" "}
                    <span
                      style={{
                        marginLeft: 4,
                        color: assignedFilter === key ? "#1677ff" : "#8c8c8c",
                        fontWeight: 600,
                      }}
                    >
                      ({count})
                    </span>
                  </span>
                ),
              };
            })}
          />
          <Table
            // Remount the table whenever the user hits "Limpiar
            // filtros" so antd's internally-owned column-filter
            // state (productor/finca/contactos/etc.) resets to "no
            // filter applied" without needing controlled mode.
            key={tableResetCounter}
            dataSource={(() => {
              // Rows are scoped by the auditor selector (drives both
              // table + stats) and further trimmed by the assignment-
              // state Segmented + the global search box. The rest of
              // the filters live in the column headers and are managed
              // by antd, so they don't show up here.
              let rows = scopedAudits;
              if (assignedFilter !== "all") {
                rows = rows.filter((audit: Audit) =>
                  matchesAssignedFilter(audit as any, assignedFilter),
                );
              }
              if (aforoFilter !== "all") {
                rows = rows.filter((audit: Audit) => {
                  const sent = Boolean((audit as any).aforo_synced_at);
                  return aforoFilter === "sent" ? sent : !sent;
                });
              }
              if (debouncedAuditSearchText) {
                const searchLower = debouncedAuditSearchText.toLowerCase();
                rows = rows.filter((audit: Audit) => {
                  const fields = [
                    (audit as any).producer_name,
                    (audit as any).farm_name,
                    (audit as any).town_name,
                    (audit as any).province_name,
                    audit.reference,
                    (audit as any).audit_type_name,
                    (audit as any).auditor_name,
                    // Contact fields — let the global search hit any
                    // of name / phone / email for either chain so the
                    // coordinator can paste a number or address and
                    // pinpoint the row.
                    (audit as any).license_contact_name,
                    (audit as any).license_contact_phone,
                    (audit as any).license_contact_email,
                    (audit as any).farm_contact_name,
                    (audit as any).farm_contact_phone,
                    (audit as any).farm_contact_email,
                  ];
                  return fields.some((value) =>
                    (value || "").toString().toLowerCase().includes(searchLower),
                  );
                });
              }
              return rows;
            })()}
            loading={loadingAudits}
            pagination={{
              current: auditCurrentPage,
              pageSize: auditPageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '15', '20', '50', '100'],
              showTotal: (total) => `${t('common.total') || 'Total'}: ${total}`,
              // Sin `onChange` propio — la paginación se sincroniza a
              // través de `handleAuditsTableChange` (Table.onChange),
              // que recibe el nuevo `pagination` en el MISMO tick que
              // filters/sorter y escribe la URL con
              // `setSearchParams(prev => ...)`. Tener dos handlers
              // provocaba race y anulaba el cambio de página.
            }}
            rowKey="id"
            size="small"
            scroll={{ x: 2200 }}
            className="planning-audits-table"
            onChange={handleAuditsTableChange}
            rowSelection={
              // Solo admin y auditor jefe pueden seleccionar filas
              // para asignación masiva. El auditor normal no ve nada.
              (isAdmin || isCoordinator)
                ? {
                    selectedRowKeys: selectedAuditIds,
                    onChange: (keys) => setSelectedAuditIds(keys as number[]),
                    preserveSelectedRowKeys: true,
                    // Filas canceladas o finalizadas no se pueden
                    // asignar — el backend las rechazaría igual, mejor
                    // deshabilitar el checkbox en la UI.
                    getCheckboxProps: (record: any) => {
                      const stateName = record?.audit_state_name || "";
                      const isCancelled = /cancelad/i.test(stateName);
                      const isFinalized =
                        record?.status_of_audit === "finalized" ||
                        /finaliz/i.test(stateName);
                      return { disabled: isCancelled || isFinalized };
                    },
                    columnWidth: 44,
                    fixed: "left",
                  }
                : undefined
            }
            onRow={(record: Audit) => ({
              onClick: (event) => {
                // El checkbox de selección masiva vive dentro de la fila y
                // el click borbujea aquí — sin este guard, marcar el check
                // para asignar navegaría al detalle. Ignoramos clicks que
                // vengan de la columna de selección de antd.
                const target = event.target as HTMLElement | null;
                if (
                  target?.closest(".ant-checkbox-wrapper") ||
                  target?.closest(".ant-table-selection-column")
                ) {
                  return;
                }
                const planningGroup = data?.data;
                navigate(`/audits/${record.id}`, {
                  state: {
                    from: location.pathname,
                    fromPlanningId: planningGroup?.id,
                    planningName: planningGroup?.name,
                    // Persistimos también la query string (?page, ?pageSize,
                    // ?tab, ?state, ?q...) para que al volver del detalle
                    // el user aterrice en la misma página, filtros y tab
                    // que dejó — antes se perdía y siempre reabría el
                    // planning en página 1.
                    fromPlanningSearch: location.search,
                  },
                });
              },
              // Fondo verde claro para las audits ya sincronizadas con
              // Certification — señal visual de qué kilos están ya en el
              // pipeline sin tener que abrir el detalle.
              className: (record as any)?.aforo_synced_at
                ? "planning-audit-row-aforo-synced"
                : undefined,
              style: { cursor: "pointer" },
            })}
          >
            <Table.Column
              dataIndex="producer_name"
              title={t("audit.producer") || "Productor"}
              key="producer_name"
              {...columnStateProps("producer_name")}
              sorter={(a: any, b: any) => (a.producer_name || '').localeCompare(b.producer_name || '')}
              // Default A→Z sort. The Table remounts via `key` on
              // "Limpiar filtros", so this default is restored for
              // free whenever the user resets.
              defaultSortOrder="ascend"
              filters={producerFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                (record.producer_name || "") === String(value)
              }
              width={150}
              render={(v: string | null) => v || "—"}
            />
            <Table.Column
              dataIndex="farm_name"
              title={t("audit.farm") || "Finca"}
              key="farm_name"
              {...columnStateProps("farm_name")}
              sorter={(a: any, b: any) => (a.farm_name || '').localeCompare(b.farm_name || '')}
              filters={farmFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                (record.farm_name || "") === String(value)
              }
              width={150}
              render={(v: string | null) => v || "—"}
            />
            <Table.Column
              dataIndex="province_name"
              title={t("audit.province") || "Provincia"}
              key="province_name"
              {...columnStateProps("province_name")}
              sorter={(a: any, b: any) =>
                (a.province_name || "").localeCompare(b.province_name || "")
              }
              filters={provinceFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                (record.province_name || "") === String(value)
              }
              width={130}
              render={(v: string | null) => v || "—"}
            />
            <Table.Column
              dataIndex="town_name"
              title={t("audit.town") || "Municipio"}
              key="town_name"
              {...columnStateProps("town_name")}
              sorter={(a: any, b: any) => (a.town_name || '').localeCompare(b.town_name || '')}
              filters={townFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                (record.town_name || "") === String(value)
              }
              width={130}
              render={(value: string | null) => value || "—"}
            />
            <Table.Column
              dataIndex="reference"
              title={t("audit.reference") || "Referencia"}
              key="reference"
              {...columnStateProps("reference")}
              sorter={(a: any, b: any) => (a.reference || '').localeCompare(b.reference || '')}
              width={120}
            />
            <Table.Column
              dataIndex="audit_type_name"
              title={t("audit.type") || "Tipo"}
              key="audit_type_name"
              {...columnStateProps("audit_type_name")}
              sorter={(a: any, b: any) => (a.audit_type_name || '').localeCompare(b.audit_type_name || '')}
              filters={auditTypeFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                (record.audit_type_name || "") === String(value)
              }
              width={130}
              render={(value: string) => value || "—"}
            />
            <Table.Column
              key="audit_state_name"
              title={t("audit.state") || "Estado"}
              width={140}
              align="center"
              {...columnStateProps("audit_state_name")}
              // Sort + filter both use the derived display state so
              // "Sin asignar" / "Asignada" rows cluster correctly and
              // the dropdown matches the cell.
              sorter={(a: any, b: any) =>
                getDisplayState(a).localeCompare(getDisplayState(b))
              }
              filters={stateFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                getDisplayState(record) === String(value)
              }
              render={(_: unknown, record: any) => {
                const display = getDisplayState(record);
                if (!display) {
                  return <span style={{ color: "#bfbfbf" }}>—</span>;
                }
                const tag = (
                  <Tag color={colorForDisplayState(display)} style={{ margin: 0 }}>
                    {display}
                  </Tag>
                );
                // Canceled audits keep the why/who/when tooltip.
                if (record?.status_of_audit === "canceled") {
                  const dateStr = record.cancellation_date
                    ? dayjs(record.cancellation_date).format("DD/MM/YYYY")
                    : "—";
                  const by = record.cancelled_by_name || "—";
                  const reason = record.cancellation_reason || "";
                  return (
                    <Tooltip
                      title={
                        <div>
                          <div>
                            <strong>{t("audit.cancelled_on", "Cancelada el")}:</strong> {dateStr}
                          </div>
                          <div>
                            <strong>{t("audit.cancelled_by", "Por")}:</strong> {by}
                          </div>
                          {reason && (
                            <div style={{ marginTop: 4 }}>
                              <strong>{t("audit.cancellation_reason", "Motivo")}:</strong> {reason}
                            </div>
                          )}
                        </div>
                      }
                    >
                      {tag}
                    </Tooltip>
                  );
                }
                // Derived states (Sin asignar / Asignada) get a
                // tooltip showing the catalogue value so the
                // override semantics aren't lost.
                const raw = record?.audit_state_name || "";
                if (display !== raw && raw) {
                  return (
                    <Tooltip
                      title={t(
                        "coordinator.derived_state_tooltip",
                        "Estado oficial: {{state}}. Mostrada como «{{display}}» según auditor y fecha de planificación.",
                        { state: raw, display },
                      )}
                    >
                      {tag}
                    </Tooltip>
                  );
                }
                return tag;
              }}
            />
            <Table.Column
              dataIndex="farm_surface_tango"
              title={
                isAdmin
                  ? t("audit.surface_licensed", "Sup. licenciada (ha)")
                  : t("audit.surface") || "Superficie (ha)"
              }
              key="farm_surface_tango"
              align="right"
              {...columnStateProps("farm_surface_tango")}
              sorter={(a: any, b: any) => {
                const aVal = typeof a.farm_surface_tango === 'string' ? parseFloat(a.farm_surface_tango) : (a.farm_surface_tango || 0);
                const bVal = typeof b.farm_surface_tango === 'string' ? parseFloat(b.farm_surface_tango) : (b.farm_surface_tango || 0);
                return aVal - bVal;
              }}
              width={120}
              render={(value: any) => {
                if (!value) return '0.00';
                const numValue = typeof value === 'string' ? parseFloat(value) : value;
                return !isNaN(numValue) ? numValue.toFixed(2) : '0.00';
              }}
            />
            {isAdmin && (
              <Table.Column
                dataIndex="audited_surface"
                title={t("audit.surface_audited", "Sup. auditada (ha)")}
                key="audited_surface"
                align="right"
                width={130}
                {...columnStateProps("audited_surface")}
                sorter={(a: any, b: any) =>
                  (Number(a.audited_surface) || 0) -
                  (Number(b.audited_surface) || 0)
                }
                render={(value: any, record: any) => {
                  const audited =
                    value != null && !isNaN(Number(value)) ? Number(value) : 0;
                  const licensed = Number(record.farm_surface_tango) || 0;
                  const diff = audited - licensed;
                  if (audited === 0)
                    return <span style={{ color: "#bfbfbf" }}>—</span>;
                  const color =
                    licensed === 0
                      ? undefined
                      : Math.abs(diff) < 0.01
                        ? undefined
                        : diff > 0
                          ? "#52c41a"
                          : "#cf1322";
                  return (
                    <Tooltip
                      title={
                        licensed === 0
                          ? undefined
                          : `Δ ${diff >= 0 ? "+" : ""}${diff.toFixed(2)} ha vs licenciada`
                      }
                    >
                      <span style={{ color, fontWeight: 500 }}>
                        {audited.toFixed(2)}
                      </span>
                    </Tooltip>
                  );
                }}
              />
            )}
            {isAdmin && (
              <Table.Column
                dataIndex="stimated_production"
                title={t("audit.producer_estimation", "Estim. productor (kg)")}
                key="stimated_production"
                align="right"
                width={150}
                {...columnStateProps("stimated_production")}
                sorter={(a: any, b: any) =>
                  (Number(a.stimated_production) || 0) -
                  (Number(b.stimated_production) || 0)
                }
                render={(value: any) => {
                  const n = Number(value) || 0;
                  return n
                    ? n.toLocaleString("es-ES", { maximumFractionDigits: 0 })
                    : "—";
                }}
              />
            )}
            {isAdmin && (
              <Table.Column
                dataIndex="audited_kg"
                title={t("audit.auditor_estimation", "Estim. auditor (kg)")}
                key="audited_kg"
                align="right"
                width={150}
                {...columnStateProps("audited_kg")}
                sorter={(a: any, b: any) =>
                  (Number(a.audited_kg) || 0) -
                  (Number(b.audited_kg) || 0)
                }
                render={(value: any, record: any) => {
                  const audited = Number(value) || 0;
                  const producer = Number(record.stimated_production) || 0;
                  if (!audited)
                    return <span style={{ color: "#bfbfbf" }}>—</span>;
                  const diff = audited - producer;
                  const color =
                    producer === 0
                      ? undefined
                      : Math.abs(diff) < 1
                        ? undefined
                        : diff > 0
                          ? "#52c41a"
                          : "#cf1322";
                  return (
                    <Tooltip
                      title={
                        producer === 0
                          ? undefined
                          : `Δ ${diff >= 0 ? "+" : ""}${diff.toLocaleString("es-ES", { maximumFractionDigits: 0 })} kg vs productor`
                      }
                    >
                      <span style={{ color, fontWeight: 500 }}>
                        {audited.toLocaleString("es-ES", {
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    </Tooltip>
                  );
                }}
              />
            )}
            <Table.Column
              key="auditor_name"
              title={t("audit.auditor") || "Auditor"}
              {...columnStateProps("auditor_name")}
              sorter={(a: any, b: any) => (a.auditor_name || '').localeCompare(b.auditor_name || '')}
              filters={auditorFilterOpts}
              filterSearch
              onFilter={(value, record: any) => {
                if (value === "__unassigned__") return record.auditor_user == null;
                return String(record.auditor_user ?? "") === String(value);
              }}
              width={160}
              render={(_: unknown, record: any) =>
                record.auditor_user == null ? (
                  <Tag color="gold" style={{ margin: 0 }}>
                    {t("coordinator.no_auditor", "Sin asignar")}
                  </Tag>
                ) : (
                  <span>{record.auditor_name || `#${record.auditor_user}`}</span>
                )
              }
            />
            <Table.Column
              dataIndex="planning_date"
              title={t("audit.planning_date") || "Fecha Planificada"}
              key="planning_date"
              {...columnStateProps("planning_date")}
              sorter={(a: any, b: any) => {
                if (!a.planning_date && !b.planning_date) return 0;
                if (!a.planning_date) return 1;
                if (!b.planning_date) return -1;
                return new Date(a.planning_date).getTime() - new Date(b.planning_date).getTime();
              }}
              width={140}
              render={(value: string) => (value ? dayjs(value).format("DD/MM/YYYY") : '-')}
            />
            <Table.Column
              dataIndex="start_date"
              title={t("audit.start_date") || "Fecha Auditoría"}
              key="start_date"
              {...columnStateProps("start_date")}
              sorter={(a: any, b: any) => {
                if (!a.start_date && !b.start_date) return 0;
                if (!a.start_date) return 1;
                if (!b.start_date) return -1;
                return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
              }}
              width={130}
              render={(value: string) => (value ? dayjs(value).format("DD/MM/YYYY") : '-')}
            />
            {/* Columna "Muestras" — muestra el par
                `creadas / planificadas` (Sample v2 registradas en la
                audit versus `samples_to_take` planificado). El sort y
                filtro operan sobre las creadas: es la métrica activa
                que interesa al coordinador ("¿ha creado ya alguna?"),
                mientras que `samples_to_take` es sólo referencia. */}
            <Table.Column
              dataIndex="samples_taken_count"
              title={t("audit.samples", "Muestras")}
              key="samples_taken_count"
              align="center"
              width={130}
              {...columnStateProps("samples_taken_count")}
              sorter={(a: any, b: any) =>
                (a.samples_taken_count || 0) - (b.samples_taken_count || 0)
              }
              filters={[
                { text: t("audit.samples_filter_created_yes", "Con muestras creadas"), value: "yes" },
                { text: t("audit.samples_filter_created_no", "Sin muestras creadas"), value: "no" },
              ]}
              filterMultiple={false}
              onFilter={(value, record: any) => {
                const created = (record.samples_taken_count || 0) > 0;
                return value === "yes" ? created : !created;
              }}
              render={(_value: number, record: any) => {
                const created = record.samples_taken_count || 0;
                const planned = record.samples_to_take || 0;
                const color = created > 0 ? "blue" : "default";
                return (
                  <Tooltip
                    title={t(
                      "audit.samples_combined_tooltip",
                      "Creadas / Planificadas",
                    )}
                  >
                    <Tag color={color}>
                      {`${created} / ${planned}`}
                    </Tag>
                  </Tooltip>
                );
              }}
            />
            {/* Contact info split into independent sortable +
                filterable columns so the coordinator can sort by
                phone or copy an email straight from a cell. Oculto
                para administradores — su flujo no necesita los
                contactos y libera ancho para las estimaciones. */}
            {!isAdmin && (
            <Table.Column
              dataIndex="license_contact_name"
              title={t("audit.license_contact_name", "Contacto Licencia")}
              key="license_contact_name"
              width={180}
              {...columnStateProps("license_contact_name")}
              sorter={(a: any, b: any) =>
                (a.license_contact_name || "").localeCompare(b.license_contact_name || "")
              }
              filters={licenseContactNameFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                (record.license_contact_name || "") === String(value)
              }
              render={(v: string | null) => v || "—"}
            />
            )}
            {!isAdmin && (
            <Table.Column
              dataIndex="license_contact_phone"
              title={t("audit.license_contact_phone", "Tel. Licencia")}
              key="license_contact_phone"
              width={150}
              {...columnStateProps("license_contact_phone")}
              sorter={(a: any, b: any) =>
                (a.license_contact_phone || "").localeCompare(b.license_contact_phone || "")
              }
              filters={licenseContactPhoneFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                (record.license_contact_phone || "") === String(value)
              }
              render={(v: string | null) =>
                v ? (
                  <a
                    href={`tel:${v}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: "#1677ff" }}
                  >
                    {v}
                  </a>
                ) : (
                  "—"
                )
              }
            />
            )}
            {!isAdmin && (
            <Table.Column
              dataIndex="license_contact_email"
              title={t("audit.license_contact_email", "Email Licencia")}
              key="license_contact_email"
              width={220}
              {...columnStateProps("license_contact_email")}
              sorter={(a: any, b: any) =>
                (a.license_contact_email || "").localeCompare(b.license_contact_email || "")
              }
              filters={licenseContactEmailFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                (record.license_contact_email || "") === String(value)
              }
              render={(v: string | null) =>
                v ? (
                  <a
                    href={`mailto:${v}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: "#1677ff" }}
                    title={v}
                  >
                    {v}
                  </a>
                ) : (
                  "—"
                )
              }
            />
            )}
            {!isAdmin && (
            <Table.Column
              dataIndex="farm_contact_name"
              title={t("audit.farm_contact_name", "Contacto Finca")}
              key="farm_contact_name"
              width={180}
              {...columnStateProps("farm_contact_name")}
              sorter={(a: any, b: any) =>
                (a.farm_contact_name || "").localeCompare(b.farm_contact_name || "")
              }
              filters={farmContactNameFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                (record.farm_contact_name || "") === String(value)
              }
              render={(v: string | null) => v || "—"}
            />
            )}
            {!isAdmin && (
            <Table.Column
              dataIndex="farm_contact_phone"
              title={t("audit.farm_contact_phone", "Tel. Finca")}
              key="farm_contact_phone"
              width={150}
              {...columnStateProps("farm_contact_phone")}
              sorter={(a: any, b: any) =>
                (a.farm_contact_phone || "").localeCompare(b.farm_contact_phone || "")
              }
              filters={farmContactPhoneFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                (record.farm_contact_phone || "") === String(value)
              }
              render={(v: string | null) =>
                v ? (
                  <a
                    href={`tel:${v}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: "#1677ff" }}
                  >
                    {v}
                  </a>
                ) : (
                  "—"
                )
              }
            />
            )}
            {!isAdmin && (
            <Table.Column
              dataIndex="farm_contact_email"
              title={t("audit.farm_contact_email", "Email Finca")}
              key="farm_contact_email"
              width={220}
              {...columnStateProps("farm_contact_email")}
              sorter={(a: any, b: any) =>
                (a.farm_contact_email || "").localeCompare(b.farm_contact_email || "")
              }
              filters={farmContactEmailFilterOpts}
              filterSearch
              onFilter={(value, record: any) =>
                (record.farm_contact_email || "") === String(value)
              }
              render={(v: string | null) =>
                v ? (
                  <a
                    href={`mailto:${v}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: "#1677ff" }}
                    title={v}
                  >
                    {v}
                  </a>
                ) : (
                  "—"
                )
              }
            />
            )}
            <Table.Column
              title={t("common.actions") || "Acciones"}
              key="actions"
              fixed="right"
              width={190}
              onCell={() => ({
                // Action cells must not trigger the row's navigate-to-detail
                // onClick. Stop propagation at the cell level so any button
                // inside (even those wrapped by Popconfirm) is safe.
                onClick: (e) => e.stopPropagation(),
              })}
              render={(_, record: Audit) => {
                const isCancelled =
                  (record as any).status_of_audit === "canceled";
                const bundleSupported = isBundleSupported(record.audit_type);
                return (
                <Space size="small">
                  {!isAuditorOnly && !isCancelled && (
                    <Button
                      size="small"
                      icon={<UserOutlined />}
                      onClick={() => handleAssignAuditor(record)}
                      title={t("audit.assign_auditor") || "Asignar Auditor"}
                    />
                  )}
                  {/* Auditor users (and coordinators) can set the
                      planning_date for each audit row from here —
                      lightweight one-field modal. Hidden for
                      cancelled / finalized rows so it stays clean. */}
                  {(isAuditorOnly || isCoordinator)
                    && !isCancelled
                    && (record as any).status_of_audit !== "finalized" && (
                    <Button
                      size="small"
                      icon={<CalendarOutlined />}
                      onClick={() => openPlanningDateModal(record)}
                      title={
                        record.planning_date
                          ? t(
                              "audit.change_planning_date",
                              "Cambiar fecha de planificación",
                            )
                          : t(
                              "audit.set_planning_date",
                              "Asignar fecha de planificación",
                            )
                      }
                    />
                  )}
                  <Button
                    size="small"
                    icon={<CloudDownloadOutlined />}
                    loading={downloadingBundleId === record.id}
                    disabled={!bundleSupported}
                    onClick={() => handleDownloadBundle(record)}
                    title={
                      bundleSupported
                        ? t("audit.download_bundle", "Descargar Excel + PDF mapa")
                        : t(
                            "audit.bundle_not_supported",
                            "Solo disponible para auditorías de tipo Completa / Aforo / Localización / Trazabilidad",
                          )
                    }
                  />
                  {isAdmin && !isCancelled && (
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEditAudit(record)}
                      title={t("common.edit") || "Editar"}
                    />
                  )}
                  {isCoordinator && !isCancelled && (
                    <Button
                      size="small"
                      danger
                      icon={<StopOutlined />}
                      onClick={() => handleCancelAudit(record)}
                      title={t("audit.cancel", "Cancelar auditoría")}
                    />
                  )}
                  {isAdmin && (
                    <Popconfirm
                      title={t("audit.delete_confirm") || "¿Está seguro de eliminar esta auditoría?"}
                      onConfirm={() => handleDeleteAudit(record.id)}
                      okText={t("common.yes") || "Sí"}
                      cancelText={t("common.no") || "No"}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        title={t("common.delete") || "Eliminar"}
                      />
                    </Popconfirm>
                  )}
                </Space>
                );
              }}
            />
          </Table>
        </Card>

      {/* Cards de Rutas GPS y Waypoints — simétricas en anchura (xs=24
          / lg=12) y en altura (flex column con `body: { flex: 1 }`).
          Las tablas internas mantienen paginación fija así que
          diferencias grandes de datos no rompen la simetría. */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }} align="stretch">
        <Col xs={24} lg={12} style={{ display: "flex" }}>
      {/* GPS Routes Section */}
      <Card
        title={t("planning.gps_routes") || "Rutas GPS"}
        bordered={false}
        style={{ display: "flex", flexDirection: "column", width: "100%" }}
        styles={{ body: { flex: 1 } }}
        extra={
          <Space>
            <Input
              placeholder={t("route.search_placeholder") || "Buscar por código de ruta o auditor..."}
              prefix={<SearchOutlined />}
              value={routeSearchText}
              onChange={(e) => setRouteSearchText(e.target.value)}
              allowClear
              style={{ width: 300 }}
            />
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setRouteUploadModalVisible(true)}
            >
              {t("route.upload_route") || "Subir Ruta"}
            </Button>
          </Space>
        }
        >
          <Table
            // NO spread {...routeTableProps} — arrastraba el `onChange`
            // interno de Refine, que al cambiar de página forzaba su
            // paginación server-side interna y en el siguiente render
            // pisaba nuestro estado local `routeCurrentPage`. Extraemos
            // sólo `dataSource` y `loading`; el resto lo controlamos
            // aquí de forma cliente-side pura sobre el dataset completo
            // que Refine ya trajo (`pageSize:10000` en el useTable).
            dataSource={(routeTableProps.dataSource || []).filter((route: Route) => {
              if (!routeSearchText) return true;
              const searchLower = routeSearchText.toLowerCase();
              return (
                route.code?.toLowerCase().includes(searchLower) ||
                route.auditor_name?.toLowerCase().includes(searchLower)
              );
            })}
            loading={routeTableProps.loading}
            pagination={{
              current: routeCurrentPage,
              pageSize: routePageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `${t('common.total') || 'Total'}: ${total}`,
            }}
            onChange={(pagination) => {
              // Único punto de escritura de la paginación local.
              // No tocamos filtros/sort en URL (esta tabla no los
              // persiste), solo sincronizamos current/pageSize.
              if (pagination?.current) setRouteCurrentPage(pagination.current);
              if (pagination?.pageSize) setRoutePageSize(pagination.pageSize);
            }}
            rowKey="id"
            size="small"
            className="planning-audits-table"
            scroll={{ x: 800 }}
          >
            <Table.Column
              dataIndex="code"
              title={t("route.code") || "Código de Ruta"}
              key="code"
              sorter
            />
            <Table.Column
              dataIndex="route_date"
              title={t("route.completion_date") || "Fecha Realización"}
              key="route_date"
              sorter
              render={(value: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "-")}
            />
            <Table.Column
              dataIndex="auditor_name"
              title={t("route.auditor") || "Auditor"}
              key="auditor_name"
              // Sorter client-side: `RouteViewSet.ordering_fields`
              // no incluye `auditor_name`, así que un `sorter` boolean
              // (server-side) no ordena nada. Aquí ordenamos por el
              // string ya resuelto que trae el serializer.
              sorter={(a: Route, b: Route) =>
                (a.auditor_name || "").localeCompare(b.auditor_name || "")
              }
            />
            <Table.Column
              dataIndex="distance"
              title={t("route.distance") || "Distancia"}
              key="distance"
              align="right"
              sorter
              render={(value: any) => {
                if (value == null) return "-";
                const numValue = typeof value === 'string' ? parseFloat(value) : value;
                return !isNaN(numValue) ? `${numValue.toFixed(2)} km` : "-";
              }}
            />
            <Table.Column
              title={t("common.actions") || "Acciones"}
              key="actions"
              fixed="right"
              width={150}
              render={(_, record: Route) => (
                <Space size="small">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setRouteToEdit(record);
                      setRouteEditModalVisible(true);
                    }}
                    title={t("common.edit") || "Editar"}
                  />
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadGPX(record)}
                    title={t("route.download_gpx") || "Descargar GPX"}
                  />
                  <Popconfirm
                    title={t("route.delete_confirm") || "¿Está seguro de eliminar esta ruta?"}
                    onConfirm={() => handleDeleteRoute(record.id)}
                    okText={t("common.yes") || "Sí"}
                    cancelText={t("common.no") || "No"}
                  >
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      title={t("common.delete") || "Eliminar"}
                    />
                  </Popconfirm>
                </Space>
              )}
            />
          </Table>
        </Card>
        </Col>
        <Col xs={24} lg={12} style={{ display: "flex" }}>

      {/* Waypoints Section */}
      <Card
        title={t("planning.waypoints") || "Waypoints"}
        bordered={false}
        style={{ display: "flex", flexDirection: "column", width: "100%" }}
        styles={{ body: { flex: 1 } }}
        extra={
          <Space>
            <Input
              placeholder={t("waypoint.search_placeholder") || "Buscar por auditor..."}
              prefix={<SearchOutlined />}
              value={waypointSearchText}
              onChange={(e) => setWaypointSearchText(e.target.value)}
              allowClear
              style={{ width: 300 }}
            />
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setWaypointUploadModalVisible(true)}
            >
              {t("waypoint.upload_waypoints") || "Subir Waypoints"}
            </Button>
          </Space>
        }
      >
        <Table
          // NO spread {...waypointTableProps} — mismo motivo que en la
          // tabla de rutas: el `onChange` interno de Refine pisaba el
          // estado local al cambiar de página. Extraemos sólo lo que
          // necesitamos y controlamos todo cliente-side sobre el
          // dataset completo.
          dataSource={(waypointTableProps.dataSource || []).filter((waypoint: any) => {
            if (!waypointSearchText) return true;
            const searchLower = waypointSearchText.toLowerCase();
            return (
              waypoint.code?.toLowerCase().includes(searchLower) ||
              waypoint.auditor?.name?.toLowerCase().includes(searchLower)
            );
          })}
          loading={waypointTableProps.loading}
          rowKey="id"
          size="small"
          className="planning-audits-table"
          scroll={{ x: 800 }}
          pagination={{
            current: waypointCurrentPage,
            pageSize: waypointPageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `${t('common.total') || 'Total'}: ${total}`,
          }}
          onChange={(pagination) => {
            if (pagination?.current) setWaypointCurrentPage(pagination.current);
            if (pagination?.pageSize) setWaypointPageSize(pagination.pageSize);
          }}
        >
          <Table.Column
            dataIndex="code"
            title={t("waypoint.code") || "Código"}
            key="code"
            sorter
          />
          <Table.Column
            dataIndex="waypoint_date"
            title={t("waypoint.date") || "Fecha"}
            key="waypoint_date"
            sorter
            render={(value: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "-")}
          />
          <Table.Column
            dataIndex={["auditor", "name"]}
            title={t("waypoint.auditor") || "Auditor"}
            key="auditor"
            // Sorter client-side: `WaypointViewSet.ordering_fields`
            // no incluye `auditor` ni el path anidado `auditor__name`;
            // el sorter boolean server-side no hacía nada. Ordenamos
            // por el nombre ya resuelto por el serializer.
            sorter={(a: any, b: any) =>
              (a?.auditor?.name || "").localeCompare(b?.auditor?.name || "")
            }
          />
          <Table.Column
            dataIndex="waypoints_count"
            title={t("waypoint.count") || "Nº Waypoints"}
            key="waypoints_count"
            align="center"
            // Sorter client-side: `waypoints_count` es un valor
            // calculado por el serializer, no un ordering_field del
            // ViewSet. El sort boolean no funcionaba.
            sorter={(a: any, b: any) =>
              (Number(a?.waypoints_count) || 0) -
              (Number(b?.waypoints_count) || 0)
            }
          />
          <Table.Column
            title={t("common.actions") || "Acciones"}
            key="actions"
            fixed="right"
            width={220}
            render={(_, record: any) => (
              <Space size="small">
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewWaypointDetail(record.id)}
                  title={t("waypoint.view_detail") || "Ver Detalle"}
                />
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setWaypointToEdit(record);
                    setWaypointEditModalVisible(true);
                  }}
                  title={t("common.edit") || "Editar"}
                />
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadWaypointGPX(record)}
                  title={t("waypoint.download_gpx") || "Descargar GPX"}
                />
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={relookupingWpId === record.id}
                  onClick={() => handleRelookupWaypointSamples(record.id, record.code)}
                  title={t(
                    "waypoint.relookup_samples",
                    "Reprocesar muestras del planning sin geometría e intentar vincularlas a los puntos de este GPX",
                  )}
                />
                <Popconfirm
                  title={t("waypoint.delete_confirm") || "¿Está seguro de eliminar este archivo de waypoints?"}
                  description={t("waypoint.delete_warning") || "Se eliminarán todos los waypoints asociados"}
                  onConfirm={() => handleDeleteWaypoint(record.id)}
                  okText={t("common.yes") || "Sí"}
                  cancelText={t("common.no") || "No"}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    title={t("common.delete") || "Eliminar"}
                  />
                </Popconfirm>
              </Space>
            )}
          />
        </Table>
      </Card>

        </Col>
      </Row>

      {/* Modals (hoisted out of the Row so antd Row only contains Cols) */}
      <AuditCreateModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        planningId={record?.id ?? 0}
        seasonId={record?.season?.id ?? 0}
        countryId={record?.country?.id}
        onSuccess={handleCreateSuccess}
      />

      <AuditEditModal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        audit={selectedAudit}
        planningId={record?.id ? Number(record.id) : undefined}
        countryId={record?.country?.id}
        onSuccess={handleUpdateSuccess}
      />

      {record && (
        <RouteUploadModal
          visible={routeUploadModalVisible}
          onClose={() => setRouteUploadModalVisible(false)}
          planningId={record.id}
          auditors={record.users || []}
          auditCompanyId={record.audit_company ?? null}
          countryId={record.country?.id ?? record.season?.country?.id ?? null}
          isAuditorOnly={isAuditorOnly}
          currentUser={currentAppUser}
          onSuccess={handleRouteUploadSuccess}
        />
      )}

      {record && (
        <RouteEditModal
          visible={routeEditModalVisible}
          onClose={() => {
            setRouteEditModalVisible(false);
            setRouteToEdit(null);
          }}
          route={routeToEdit}
          auditors={record.users || []}
          auditCompanyId={record.audit_company ?? null}
          countryId={record.country?.id ?? record.season?.country?.id ?? null}
          isAuditorOnly={isAuditorOnly}
          currentUser={currentAppUser}
          onSuccess={handleRouteUploadSuccess}
        />
      )}

      {record && (
        <UploadWaypointsModal
          visible={waypointUploadModalVisible}
          onClose={() => setWaypointUploadModalVisible(false)}
          planningId={record.id}
          auditors={record.users || []}
          auditCompanyId={record.audit_company ?? null}
          countryId={record.country?.id ?? record.season?.country?.id ?? null}
          isAuditorOnly={isAuditorOnly}
          currentUser={currentAppUser}
          onSuccess={handleWaypointUploadSuccess}
        />
      )}

      {record && (
        <WaypointEditModal
          visible={waypointEditModalVisible}
          onClose={() => {
            setWaypointEditModalVisible(false);
            setWaypointToEdit(null);
          }}
          waypoint={waypointToEdit}
          auditors={record.users || []}
          auditCompanyId={record.audit_company ?? null}
          countryId={record.country?.id ?? record.season?.country?.id ?? null}
          isAuditorOnly={isAuditorOnly}
          currentUser={currentAppUser}
          onSuccess={handleWaypointUploadSuccess}
        />
      )}

      <WaypointDetailModal
        visible={waypointDetailModalVisible}
        onClose={() => {
          setWaypointDetailModalVisible(false);
          setSelectedWaypointId(null);
        }}
        waypointId={selectedWaypointId}
      />

      <Modal
        title={t("audit.planning_date_title", "Fecha de planificación")}
        open={planningDateModalVisible}
        onCancel={() => {
          if (savingPlanningDate) return;
          setPlanningDateModalVisible(false);
          setAuditForPlanningDate(null);
        }}
        onOk={submitPlanningDate}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        confirmLoading={savingPlanningDate}
        destroyOnClose
      >
        <div style={{ marginBottom: 8, color: "#8c8c8c", fontSize: 13 }}>
          {auditForPlanningDate?.reference}
          {auditForPlanningDate && (
            <>
              {" · "}
              {(auditForPlanningDate as any).producer_name || "?"}
              {" · "}
              {(auditForPlanningDate as any).farm_name || "?"}
            </>
          )}
        </div>
        <Form layout="vertical">
          <Form.Item
            label={t(
              "audit.planning_date_label",
              "Día en que tienes previsto auditar la finca",
            )}
            help={t(
              "audit.planning_date_help",
              "Vacíalo si quieres quitar la fecha asignada.",
            )}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
              value={planningDateValue}
              onChange={(d) => setPlanningDateValue(d)}
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>

      <AssignAuditorModal
        visible={assignAuditorModalVisible}
        onClose={() => {
          setAssignAuditorModalVisible(false);
          setAuditToAssign(null);
        }}
        audit={auditToAssign}
        auditors={record?.users || []}
        auditCompanyId={record?.audit_company ?? null}
        countryId={record?.country?.id ?? record?.season?.country?.id ?? null}
        onSuccess={handleAssignAuditorSuccess}
      />

      <BulkAssignAuditorModal
        visible={bulkAssignModalVisible}
        onClose={() => setBulkAssignModalVisible(false)}
        auditIds={selectedAuditIds}
        auditors={record?.users || []}
        auditCompanyId={record?.audit_company ?? null}
        countryId={record?.country?.id ?? record?.season?.country?.id ?? null}
        onSuccess={() => {
          setSelectedAuditIds([]);
          // Reuse el mismo handler para refetch (audits + planning).
          handleAssignAuditorSuccess();
        }}
      />

      <CancelAuditModal
        visible={cancelAuditModalVisible}
        onClose={() => {
          setCancelAuditModalVisible(false);
          setAuditToCancel(null);
        }}
        audit={auditToCancel}
        onSuccess={handleCancelAuditSuccess}
      />

      {/* Panel de comentarios / hilos entre auditores y coordinadores
          sobre esta planificación. Abre desde el header y permite
          deep-link vía ?tab=comments&ticket=<id> (email de notif). */}
      <Drawer
        open={commentsOpen}
        onClose={closeComments}
        title={t("planning.comments.title", "Comentarios de la planificación")}
        width={720}
        destroyOnHidden
      >
        {record?.id != null && (
          <PlanningCommentsPanel
            planningGroupId={Number(record.id)}
            initialTicketId={commentsInitialTicketId}
          />
        )}
      </Drawer>
    </Show>
  );
};
